import { useState, useEffect, useRef } from 'react';
import Papa from 'papaparse';
import MapView from './MapView';
import ChartView from './ChartView';
import ComparativeChartView from './ComparativeChartView';
import { feature } from 'topojson-client';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import { Tooltip } from 'react-tooltip';
import './OECDExplorer.css';
import '@fortawesome/fontawesome-free/css/all.min.css';

// Aggregates that are not countries and must not appear in country lists
const NON_COUNTRIES = ['OECD', 'Commonwealth', 'EU', 'EA19', 'OAVG', 'OECDC', 'EU27_2020'];

// Normalise raw CSV rows (System Components, Sub-Components, Variable, Unit of Measure, Value, Year, Country)
// into the row shape the views expect. Non-numeric values and out-of-range years are dropped.
const cleanDataRows = (rows) => rows
  .filter(row => {
    if (!row || !row.Variable || !row.Country) return false;
    if (NON_COUNTRIES.includes(row.Country)) return false;
    const numericValue = parseFloat(String(row.Value).replace(/,/g, ''));
    if (isNaN(numericValue)) return false;
    const numericYear = parseInt(row.Year);
    if (isNaN(numericYear) || numericYear < 2000 || numericYear > 2030) return false;
    return true;
  })
  .map(row => ({
    Component: (row['System Components'] || '').toString().trim(),
    'Sub-component': (row['Sub-Components'] || '').toString().trim(),
    Variable: (row.Variable || '').toString().trim(),
    Measure: (row['Unit of Measure'] || '').toString().trim(),
    Value: parseFloat(String(row.Value).replace(/,/g, '')),
    Year: parseInt(row.Year),
    Country: (row.Country || '').toString().trim()
  }))
  .filter(row => row.Component && row.Variable && row.Country && !isNaN(row.Value) && !isNaN(row.Year));

// Fixed Terms and Conditions Popup Component
const TermsAndConditionsPopup = ({ onAccept }) => {
  const [step, setStep] = useState('terms');
  const [showTerms, setShowTerms] = useState(false);
  const [termsContent, setTermsContent] = useState('');
  const [isChecked, setIsChecked] = useState(false);
  const [loadingTerms, setLoadingTerms] = useState(false);

  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [accessError, setAccessError] = useState('');
  const [loadingCredentials, setLoadingCredentials] = useState(false);
  const [validCredentials, setValidCredentials] = useState([]);
  const [credentialsLoaded, setCredentialsLoaded] = useState(false);

  // Mock credentials for testing (replace with your actual data)
  const mockCredentials = [
    { id: 'user1', password: 'pass1' },
    { id: 'admin', password: 'admin123' },
    { id: 'test', password: 'test123' }
  ];

  // Load user credentials from CSV with error handling
  const loadUserCredentials = async () => {
    if (credentialsLoaded || loadingCredentials) {
      return; // Prevent multiple loads
    }
    
    setLoadingCredentials(true);
    try {
      const response = await fetch('/user_database.csv');
      if (!response.ok) {
        throw new Error('Failed to load user credentials');
      }
      const csvText = await response.text();
      
      // Simple CSV parsing (you can use Papa Parse if available)
      const lines = csvText.split('\n');
      const credentials = [];
      
      for (let i = 1; i < lines.length; i++) { // Skip header
        const line = lines[i].trim();
        if (line) {
          const [id, password] = line.split(',');
          if (id && password) {
            credentials.push({
              id: id.trim().replace(/"/g, ''),
              password: password.trim().replace(/"/g, '')
            });
          }
        }
      }
      
      setValidCredentials(credentials);
      setCredentialsLoaded(true);
      console.log(`Loaded ${credentials.length} user credentials`);
    } catch (error) {
      console.error('Error loading user credentials:', error);
      // Fallback to mock credentials
      setValidCredentials(mockCredentials);
      setCredentialsLoaded(true);
      setAccessError('Using demo credentials. Try: user1/pass1 or admin/admin123');
    } finally {
      setLoadingCredentials(false);
    }
  };

  // Load credentials when component mounts and step changes to access
  useEffect(() => {
    if (step === 'access' && !credentialsLoaded && !loadingCredentials) {
      loadUserCredentials();
    }
  }, [step, credentialsLoaded, loadingCredentials]);

  // Load terms and conditions from TC.txt
  const loadTermsContent = async () => {
    setLoadingTerms(true);
    try {
      const response = await fetch('/TC.txt');
      if (!response.ok) {
        throw new Error('Failed to load terms and conditions');
      }
      const content = await response.text();
      setTermsContent(content);
    } catch (error) {
      console.error('Error loading terms and conditions:', error);
      setTermsContent(`Terms and Conditions

Welcome to the Health Systems Visualiser. 

By using this application, you agree to:
1. Use the data for educational and research purposes only
2. Acknowledge the source of the data in any publications
3. Not redistribute the raw data without permission
4. Report any issues or bugs to the development team

This application provides health systems data from various sources for analysis and visualization purposes.

Please contact the development team if you have any questions about these terms.`);
    } finally {
      setLoadingTerms(false);
    }
  };

  const handleTermsClick = () => {
    if (!termsContent) {
      loadTermsContent();
    }
    setShowTerms(true);
  };

  // Validate user ID and password combination
  const validateCredentials = (id, pass) => {
    return validCredentials.find(cred => 
      cred.id === id.trim() && cred.password === pass.trim()
    );
  };

  // Handle access verification
  const handleAccessSubmit = () => {
    setAccessError('');

    // Validate user ID
    if (!userId.trim()) {
      setAccessError('Please enter your ID');
      return;
    }

    // Validate password
    if (!password.trim()) {
      setAccessError('Please enter your password');
      return;
    }

    // Check credentials against loaded data
    if (validCredentials.length === 0) {
      setAccessError('User credentials not loaded. Please try again.');
      return;
    }

    const userInfo = validateCredentials(userId, password);
    if (!userInfo) {
      setAccessError('Invalid ID or password. Please check your credentials.');
      return;
    }

    // Success - show welcome message
    setStep('welcome');
  };

  const handleTermsAccept = () => {
    if (isChecked) {
      setStep('access');
    }
  };

  const handleWelcomeContinue = () => {
    onAccept();
  };

  return (
    <>
      {/* Main popup overlay */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '2rem',
          maxWidth: '500px',
          width: '100%',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          textAlign: 'center'
        }}>

          {/* Terms Step */}
          {step === 'terms' && (
            <>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                color: '#1F2937',
                marginBottom: '1.5rem',
                margin: '0 0 1.5rem 0'
              }}>
                Welcome to Health Systems Visualiser
              </h2>
              
              <p style={{
                color: '#6B7280',
                marginBottom: '2rem',
                lineHeight: '1.6'
              }}>
                Before using this application, please read and accept our terms and conditions.
              </p>

              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                marginBottom: '2rem',
                textAlign: 'left',
                gap: '0.75rem'
              }}>
                <input
                  type="checkbox"
                  id="terms-checkbox"
                  checked={isChecked}
                  onChange={(e) => setIsChecked(e.target.checked)}
                  style={{
                    marginTop: '0.25rem',
                    width: '16px',
                    height: '16px',
                    flexShrink: 0
                  }}
                />
                <label htmlFor="terms-checkbox" style={{
                  fontSize: '0.875rem',
                  color: '#374151',
                  lineHeight: '1.5'
                }}>
                  I have read and agree to the{' '}
                  <button
                    onClick={handleTermsClick}
                    style={{
                      color: '#3B82F6',
                      textDecoration: 'underline',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      padding: 0
                    }}
                  >
                    Terms and Conditions
                  </button>
                </label>
              </div>

              <button
                onClick={handleTermsAccept}
                disabled={!isChecked}
                style={{
                  backgroundColor: isChecked ? '#3B82F6' : '#D1D5DB',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: isChecked ? 'pointer' : 'not-allowed',
                  transition: 'background-color 0.2s'
                }}
              >
                Continue
              </button>
            </>
          )}

          {/* Access Step */}
          {step === 'access' && (
            <>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                color: '#1F2937',
                marginBottom: '1rem',
                margin: '0 0 1rem 0'
              }}>
                User Login
              </h2>
              
              <p style={{
                color: '#6B7280',
                marginBottom: '2rem',
                lineHeight: '1.6',
                fontSize: '0.9rem'
              }}>
                Please enter your ID and password to access the visualizer.
              </p>

              {loadingCredentials && (
                <div style={{
                  backgroundColor: '#EFF6FF',
                  border: '1px solid #BFDBFE',
                  borderRadius: '6px',
                  padding: '0.75rem',
                  marginBottom: '1rem',
                  color: '#1E40AF',
                  fontSize: '0.875rem'
                }}>
                  Loading user credentials...
                </div>
              )}

              {accessError && (
                <div style={{
                  backgroundColor: '#FEF2F2',
                  border: '1px solid #FECACA',
                  borderRadius: '6px',
                  padding: '0.75rem',
                  marginBottom: '1rem',
                  color: '#B91C1C',
                  fontSize: '0.875rem'
                }}>
                  {accessError}
                </div>
              )}

              <div style={{ marginBottom: '1rem' }}>
                <input
                  type="text"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder="ID"
                  disabled={loadingCredentials}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '2rem' }}>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  disabled={loadingCredentials}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !loadingCredentials && userId.trim() && password.trim()) {
                      handleAccessSubmit();
                    }
                  }}
                />
              </div>

              <div style={{
                display: 'flex',
                gap: '0.5rem',
                justifyContent: 'center'
              }}>
                <button
                  onClick={() => setStep('terms')}
                  disabled={loadingCredentials}
                  style={{
                    backgroundColor: '#F3F4F6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '0.75rem 1.5rem',
                    fontSize: '1rem',
                    cursor: loadingCredentials ? 'not-allowed' : 'pointer'
                  }}
                >
                  Back
                </button>
                <button
                  onClick={handleAccessSubmit}
                  disabled={!userId.trim() || !password.trim() || loadingCredentials}
                  style={{
                    backgroundColor: userId.trim() && password.trim() && !loadingCredentials ? '#3B82F6' : '#D1D5DB',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '0.75rem 2rem',
                    fontSize: '1rem',
                    fontWeight: '500',
                    cursor: userId.trim() && password.trim() && !loadingCredentials ? 'pointer' : 'not-allowed',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {loadingCredentials ? 'Loading...' : 'Access Visualizer'}
                </button>
              </div>
            </>
          )}

          {/* Welcome Step */}
          {step === 'welcome' && (
            <>
              <div style={{
                textAlign: 'center',
                marginBottom: '2rem'
              }}>
                <h2 style={{
                  fontSize: '1.75rem',
                  fontWeight: '600',
                  color: '#1F2937',
                  marginBottom: '1rem',
                  margin: '0 0 1rem 0'
                }}>
                  Welcome!
                </h2>
                
                <p style={{
                  color: '#6B7280',
                  marginBottom: '2rem',
                  lineHeight: '1.6',
                  fontSize: '1rem'
                }}>
                You have successfully logged into the Health Systems Visualizer.
                You can now explore health system data from countries around the world.
                For the best experience, we recommend using the tool on a desktop browser.
                </p>
              </div>

              <button
                onClick={handleWelcomeContinue}
                style={{
                  backgroundColor: '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.875rem 2.5rem',
                  fontSize: '1.1rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#10B981'}
              >
                Continue to Visualizer →
              </button>
            </>
          )}
        </div>
      </div>

      {/* Terms and Conditions modal */}
      {showTerms && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          zIndex: 10001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem'
        }}
        onClick={() => setShowTerms(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '800px',
            maxHeight: '80vh',
            width: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}
          onClick={(e) => e.stopPropagation()}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#1F2937'
              }}>
                Terms and Conditions
              </h3>
              <button
                onClick={() => setShowTerms(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#6B7280',
                  padding: '0.25rem'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{
              flex: 1,
              overflow: 'auto',
              border: '1px solid #E5E7EB',
              borderRadius: '4px',
              padding: '1rem',
              backgroundColor: '#F9FAFB'
            }}>
              {loadingTerms ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p>Loading terms and conditions...</p>
                </div>
              ) : (
                <pre style={{
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.875rem',
                  lineHeight: '1.6',
                  color: '#374151',
                  margin: 0,
                  fontFamily: 'inherit'
                }}>
                  {termsContent}
                </pre>
              )}
            </div>
            
            <div style={{
              marginTop: '1.5rem',
              textAlign: 'right'
            }}>
              <button
                onClick={() => setShowTerms(false)}
                style={{
                  backgroundColor: '#3B82F6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Searchable Multi-Select Component with Custom Grouping Order and Sub-headers
const SearchableMultiSelect = ({ options, selected, onToggle, onSelectAll, onDeselectAll, placeholder, id, groupings = {}, totalCountries = 0 }) => {  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Define the custom order for groupings
  const getGroupingOrder = (groupName) => {
    // WHO regional groupings (alphabetical order - using exact names from CSV)
    const whoRegionalGroups = [
      'AFRO', // African Region
      'AMRO', // Region of the Americas
      'EMRO', // Eastern Mediterranean Region
      'EURO', // European Region
      'SEARO', // South-East Asia Region
      'WPRO'  // Western Pacific Region
    ];
    
    // World Bank income groups (low to high income - using exact names from CSV)
    const worldBankGroups = [
      'LIC',    // Low Income Countries
      'LMIC',   // Lower Middle Income Countries  
      'UMIC',   // Upper Middle Income Countries
      'HIC'     // High Income Countries
    ];
    
    // Regional blocs in specified order (using exact names from CSV)
    const regionalBlocs = ['G7', 'G20', 'EU', 'OECD', 'Commonwealth'];
    
    // Assign order values
    if (whoRegionalGroups.includes(groupName)) {
      return 100 + whoRegionalGroups.indexOf(groupName);
    } else if (worldBankGroups.includes(groupName)) {
      return 200 + worldBankGroups.indexOf(groupName);
    } else if (regionalBlocs.includes(groupName)) {
      return 300 + regionalBlocs.indexOf(groupName);
    } else {
      // Any other groupings go to the end, in alphabetical order
      return 400 + groupName.charCodeAt(0);
    }
  };

  // Helper function to get section for a group
  const getGroupSection = (groupName) => {
    const whoRegionalGroups = ['AFRO', 'AMRO', 'EMRO', 'EURO', 'SEARO', 'WPRO'];
    const worldBankGroups = [
      'LIC',    // Low Income Countries
      'LMIC',   // Lower Middle Income Countries  
      'UMIC',   // Upper Middle Income Countries
      'HIC'     // High Income Countries
    ];
    const regionalBlocs = ['G7', 'G20', 'EU', 'OECD', 'Commonwealth'];
    
    if (whoRegionalGroups.includes(groupName)) {
      return 'WHO Regions';
    } else if (worldBankGroups.includes(groupName)) {
      return 'World Bank Income Groups';
    } else if (regionalBlocs.includes(groupName)) {
      return 'Regional Blocs';
    } else {
      return 'Income Groups';
    }
  };

  // Create structured options including groups with section headers
  const allOptions = (() => {
    // Sort groupings according to custom order
    const sortedGroupKeys = Object.keys(groupings).sort((a, b) => {
      return getGroupingOrder(a) - getGroupingOrder(b);
    });
    
    // Group the sorted keys by section
    const sectionMap = {};
    sortedGroupKeys.forEach(groupKey => {
      const section = getGroupSection(groupKey);
      if (!sectionMap[section]) {
        sectionMap[section] = [];
      }
      sectionMap[section].push(groupKey);
    });

    // Build options with section headers
    const groupOpts = [];
    const sectionOrder = ['WHO Regions', 'World Bank Income Groups', 'Regional Blocs', 'Income Groups'];
    
    sectionOrder.forEach(sectionName => {
      if (sectionMap[sectionName] && sectionMap[sectionName].length > 0) {
        // Add section header
        groupOpts.push({
          type: 'section-header',
          label: sectionName,
          value: `section-${sectionName}`
        });
        
        // Add groups in this section
        sectionMap[sectionName].forEach(groupKey => {
          groupOpts.push({
            type: 'group',
            label: groupKey,
            value: groupKey,
            countries: groupings[groupKey]
          });
        });
      }
    });
    
    // Add divider after groups if there are groups
    const dividerOpt = groupOpts.length > 0 ? [{
      type: 'divider',
      label: '─────────────────────',
      value: 'divider'
    }] : [];
    
    const countryOpts = options.map(c => ({
      type: 'country',
      label: c,
      value: c
    }));
    
    return [...groupOpts, ...dividerOpt, ...countryOpts];
  })();

  // Filter options based on search term
  const filteredOptions = allOptions.filter(opt => {
    if (opt.type === 'divider' || opt.type === 'section-header') return true; // Always show dividers and section headers
    return opt.label.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Determine if an option is selected
  const isSelected = (opt) => {
    if (opt.type === 'country') {
      return selected.includes(opt.value);
    } else if (opt.type === 'group') {
      return opt.countries.every(c => selected.includes(c));
    }
    return false;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="dropdown-container" id={id} ref={dropdownRef}>
      <div className="dropdown-header" onClick={() => setIsOpen(!isOpen)} style={{backgroundColor: '#ffffff', color: '#000000'}}>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={selected.length === totalCountries ? 'All Countries' :
                      selected.length === 0 ? 'No Countries Selected' :
                      `${selected.length} Countries Selected`}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(true);
          }}
          className="dropdown-search-input"
          style={{backgroundColor: '#ffffff', color: '#000000'}}
        />
        <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
      </div>
      {isOpen && (
        <div className="dropdown-menu" style={{backgroundColor: '#ffffff'}}>
          <div className="dropdown-actions">
            <button className="dropdown-action-button" onClick={onSelectAll} style={{backgroundColor: '#ffffff', color: '#000000'}}>Select All</button>
            <button className="dropdown-action-button" onClick={onDeselectAll} style={{backgroundColor: '#ffffff', color: '#000000'}}>Deselect All</button>
          </div>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt, index) => {
              if (opt.type === 'section-header') {
                return (
                  <div
                    key={index}
                    className="dropdown-section-header"
                    style={{
                      padding: '12px 16px 8px 16px',
                      color: '#6B7280',
                      fontSize: '13px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      backgroundColor: '#F9FAFB',
                      borderBottom: '1px solid #E5E7EB',
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}
                  >
                    {opt.label}
                  </div>
                );
              }
              
              if (opt.type === 'divider') {
                return (
                  <div
                    key={index}
                    className="dropdown-divider"
                    style={{
                      padding: '8px 12px',
                      color: '#999',
                      fontSize: '12px',
                      textAlign: 'center',
                      borderBottom: '1px solid #e0e0e0',
                      backgroundColor: '#f8f8f8',
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}
                  >
                    Individual Countries
                  </div>
                );
              }
              
              return (
                <div
                  key={index}
                  className={`dropdown-item ${opt.type === 'group' ? 'group-item' : ''} ${opt.type === 'country' && isSelected(opt) ? 'selected' : ''}`}
                  onClick={() => onToggle(opt)}
                  style={{
                    color: '#000000',
                    paddingLeft: opt.type === 'group' ? '24px' : '16px' // Indent groups slightly
                  }}
                >
                  {opt.type === 'country' && (
                    <input
                      type="checkbox"
                      checked={isSelected(opt)}
                      readOnly
                    />
                  )}
                  <span>{opt.type === 'group' ? opt.label : opt.label}</span>
                </div>
              );
            })
          ) : (
            <div className="dropdown-item no-options" style={{color: '#000000'}}>No countries found</div>
          )}
        </div>
      )}
    </div>
  );
};

// Main App Component
const OECDExplorer = () => {
  // State variables
  const [loading, setLoading] = useState(true);
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filteredData2, setFilteredData2] = useState([]);
  const [dataStructure, setDataStructure] = useState({
    variables: [],
    measures: [],
    countries: [],
    years: [],
    components: [],
    subComponents: [],
    variableMap: {}
  });
  const [variableDefinitions, setVariableDefinitions] = useState({});
  const [groupings, setGroupings] = useState({});
  const [metadata, setMetadata] = useState([]);
  const [legendMap, setLegendMap] = useState({});
  // Per-variable data loading: the index lists one CSV per variable under /Current/data/
  const dataFilesRef = useRef({});
  const dataVersionRef = useRef('');
  const loadedVariablesRef = useRef(new Set());
  const [loadingVariables, setLoadingVariables] = useState(0);
  const [availableMeasures, setAvailableMeasures] = useState([]);
  const [availableVariables, setAvailableVariables] = useState([]);
  const [availableSubComponents, setAvailableSubComponents] = useState([]);
  const [availableMeasures2, setAvailableMeasures2] = useState([]);
  const [availableVariables2, setAvailableVariables2] = useState([]);
  const [availableSubComponents2, setAvailableSubComponents2] = useState([]);
  const [worldGeoJSON, setWorldGeoJSON] = useState(null);
  const [countryMapping, setCountryMapping] = useState({});
  const [m49Mapping, setM49Mapping] = useState({});
  //these were for radio button style dropdown for unit of measure, che asked to be removed
  //const [showMeasureDropdown, setShowMeasureDropdown] = useState(false); 
  //const [showMeasureDropdown2, setShowMeasureDropdown2] = useState(false);
  const [activeTab, setActiveTab] = useState('chart');
  const [viewMode, setViewMode] = useState('single');
  const [selectedVariable, setSelectedVariable] = useState('');
  const [selectedMeasure, setSelectedMeasure] = useState('');
  const [selectedComponent, setSelectedComponent] = useState('');
  const [selectedSubComponent, setSelectedSubComponent] = useState('');
  const [selectedVariable2, setSelectedVariable2] = useState('');
  const [selectedMeasure2, setSelectedMeasure2] = useState('');
  const [selectedComponent2, setSelectedComponent2] = useState('');
  const [selectedSubComponent2, setSelectedSubComponent2] = useState('');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedCountries2, setSelectedCountries2] = useState([]);
  const [selectedYear, setSelectedYear] = useState(2010);
  const [chartXAxis, setChartXAxis] = useState('Year');
  const [chartType, setChartType] = useState('line');
  const [showLegend, setShowLegend] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [showMetadata, setShowMetadata] = useState(false);
  const [showData, setShowData] = useState(false);
  const [isAnimatingYear, setIsAnimatingYear] = useState(false);
  const [mapMode, setMapMode] = useState('2d');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [fontLoaded, setFontLoaded] = useState(false);

  // Refs
  const debugRef = useRef({
    mapLoaded: false,
    csvLoaded: false,
    filterApplied: false,
    errors: [],
    variableMeasures: {}
  });
  const animationInterval = useRef(null);

  // Trigger resize on panel changes
  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
    return () => clearTimeout(timer);
  }, [isSidebarOpen]);

  // Check if terms have been accepted when component mounts
  useEffect(() => {
    const termsAccepted = localStorage.getItem('termsAccepted');
    if (!termsAccepted) {
      setShowTermsPopup(true);
    }
  }, []);

  // Handle terms acceptance
  const handleTermsAccept = () => {
    setShowTermsPopup(false);
  };

  // Add debug info
  const addDebugInfo = (category, message, data = null) => {
    console.log(`[${category}] ${message}`, data || '');
    debugRef.current[category] = debugRef.current[category] || [];
    debugRef.current[category].push({
      message,
      data,
      timestamp: new Date().toISOString()
    });
  };

// Handle sorting with enhanced logic for data popup
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    addDebugInfo('sort', `Sorting table by ${key} (${direction})`);
  };
  /*
  // Export to CSV
  const exportToCSV = () => {
    const csvData = viewMode === 'single' ? filteredData : [...filteredData, ...filteredData2];
    const csv = Papa.unparse(csvData, {
      header: true,
      columns: ['Country', 'Year', 'Value', 'Measure', 'Component', 'Sub-component', 'Variable'],
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `oecd_data_${selectedVariable || 'export'}_${selectedYear || 'all'}.csv`);
    addDebugInfo('export', 'Exported filtered data to CSV', { rows: csvData.length });
  };
  */
  // Export to PNG
  const exportToPNG = async () => {
    const contentArea = document.querySelector('.content-area');
    if (!contentArea) return;
    try {
      const canvas = await html2canvas(contentArea, { scale: 2 });
      canvas.toBlob(blob => {
        saveAs(blob, `oecd_${activeTab}_${selectedVariable || 'view'}_${selectedYear || 'all'}.png`);
        addDebugInfo('export', 'Exported visualization to PNG', { tab: activeTab });
      });
    } catch (error) {
      console.error('Error exporting to PNG:', error);
      debugRef.current.errors.push({
        type: 'export',
        message: error.message,
        timestamp: new Date().toISOString(),
      });
    }
  };

  // Download PDF functions
  const downloadPrimer = () => {
    const url = '/Current/Health_Systems_Primer.pdf';
    window.open(url, '_blank');
    addDebugInfo('download', 'Opened Health Systems Primer PDF in new tab');
  };

  const downloadHandbook = () => {
    const url = '/Current/Visualizer_Handbook.pdf';
    window.open(url, '_blank');
    addDebugInfo('download', 'Opened Visualizer Handbook PDF in new tab');
  };

  // Toggle sidebar
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    
    // Update CSS custom property
    const container = document.querySelector('.explorer-container');
    if (container) {
      container.style.setProperty('--sidebar-width', !isSidebarOpen ? '300px' : '40px');
    }
    
    // Force resize events
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      if (window.Plotly) {
        document.querySelectorAll('.plotly-chart').forEach(div => {
          if (div._fullLayout) window.Plotly.Plots.resize(div);
        });
      }
    }, 100);
  };

  // Toggle metadata
  const toggleMetadata = () => {
    setShowMetadata(!showMetadata);
    addDebugInfo('ui', `Metadata toggled to ${!showMetadata ? 'visible' : 'hidden'}`);
  };

  // Helper function to get variable definition with fallback logic
  const getVariableDefinition = (variable, definitions) => {
    console.log('getVariableDefinition called with:', {
      variable,
      definitions: definitions ? Object.keys(definitions).length : 'undefined'
    });

    if (!definitions || !variable) {
      console.log('getVariableDefinition: Missing definitions or variable');
      return 'Definition not provided';
    }

    // Direct lookup first
    let definition = definitions[variable];
    console.log('getVariableDefinition: Direct lookup result:', definition);

    if (!definition || definition.trim() === '' || definition === 'Definition not provided') {
      // Try case-insensitive and trimmed match
      const normalizedSelected = variable.trim().toLowerCase();
      const matchingKey = Object.keys(definitions).find(key => 
        key.trim().toLowerCase() === normalizedSelected
      );
      console.log('getVariableDefinition: Fallback lookup:', {
        normalizedSelected,
        matchingKey,
        sampleKeys: Object.keys(definitions).slice(0, 5)
      });
      
      definition = matchingKey ? definitions[matchingKey] : null;
    }

    // Final fallback
    if (!definition || definition.trim() === '' || definition === 'Definition not provided') {
      definition = 'Definition not provided';
    }

    console.log('getVariableDefinition: Final result:', definition);
    return definition;
    };

  // Toggle data popup
  const toggleData = () => {
    setShowData(!showData);
    addDebugInfo('ui', `Data popup toggled to ${!showData ? 'visible' : 'hidden'}`);
  };

  // Get current metadata based on selections
  const getCurrentMetadata = () => {
    const getMeta = (comp, sub, varr, meas) => {
      if (!metadata.length || !varr) return null;
      
      console.log('Looking for metadata with:', { comp, sub, varr, meas });
      console.log('Available metadata entries:', metadata.length);
      console.log('Sample metadata entry:', metadata[0]);
      
      const compareStrings = (a, b) => {
        if (!a || !b) return false;
        return a.toString().trim().toLowerCase() === b.toString().trim().toLowerCase();
      };
      
      // First try exact match with all fields
      let meta = metadata.find(row => {
        const componentMatch = !comp || compareStrings(row.Component, comp);
        const subMatch = !sub || compareStrings(row['Sub-component'], sub);
        const varMatch = compareStrings(row.Variable, varr);
        const measMatch = !meas || compareStrings(row.Measure, meas);
        
        const matches = componentMatch && subMatch && varMatch && measMatch;
        
        if (matches) {
          console.log('Found exact match:', row);
        }
        
        return matches;
      });
      
      // If no exact match, try just variable match
      if (!meta) {
        meta = metadata.find(row => compareStrings(row.Variable, varr));
        if (meta) {
          console.log('Found variable-only match:', meta);
        }
      }
      
      // If still no match, try partial variable match
      if (!meta) {
        meta = metadata.find(row => 
          row.Variable && varr && 
          (row.Variable.toLowerCase().includes(varr.toLowerCase()) || 
          varr.toLowerCase().includes(row.Variable.toLowerCase()))
        );
        if (meta) {
          console.log('Found partial variable match:', meta);
        }
      }
      
      console.log('Final metadata result:', meta);
      return meta;
    };

    const meta1 = getMeta(selectedComponent, selectedSubComponent, selectedVariable, selectedMeasure);
    
    if (viewMode === 'single') {
      console.log('Single mode metadata:', meta1);
      return meta1;
    }
    
    const meta2 = getMeta(selectedComponent2, selectedSubComponent2, selectedVariable2, selectedMeasure2);
    const result = [meta1, meta2].filter(Boolean);
    console.log('Comparative mode metadata:', result);
    return result;
  };

  // Start year animation
  const startAnimation = () => {
    if (isAnimatingYear || !dataStructure.years.length) return;
    setIsAnimatingYear(true);
    addDebugInfo('animation', 'Started year animation');

    let index = dataStructure.years.indexOf(selectedYear) + 1;
    if (index >= dataStructure.years.length) index = 0;

    animationInterval.current = setInterval(() => {
      if (index >= dataStructure.years.length) index = 0;
      setSelectedYear(dataStructure.years[index]);
      addDebugInfo('animation', `Animated to year ${dataStructure.years[index]}`);
      index++;
    }, 1000);
  };

// Stop year animation
  const stopAnimation = () => {
    if (!isAnimatingYear) return;
    clearInterval(animationInterval.current);
    setIsAnimatingYear(false);
    animationInterval.current = null;
    addDebugInfo('animation', 'Stopped year animation');
  };

  // Helper function for dummy data (moved before useEffect)
  const setDummyData = () => {
    addDebugInfo('fallback', 'Using dummy data');
    const dummyData = [
      {
        Variable: "Fertility Rate",
        Measure: "Live Births per Woman",
        Country: "United States",
        Year: 2010,
        Value: 1.93,
        Component: "Health System Context",
        "Sub-component": "Demographic"
      },
      {
        Variable: "Fertility Rate",
        Measure: "Live Births per Woman",
        Country: "United States",
        Year: 2011,
        Value: 1.89,
        Component: "Health System Context",
        "Sub-component": "Demographic"
      },
      {
        Variable: "Fertility Rate",
        Measure: "Live Births per Woman",
        Country: "Canada",
        Year: 2010,
        Value: 1.63,
        Component: "Health System Context",
        "Sub-component": "Demographic"
      },
      {
        Variable: "Fertility Rate",
        Measure: "Live Births per Woman",
        Country: "Canada",
        Year: 2011,
        Value: 1.61,
        Component: "Health System Context",
        "Sub-component": "Demographic"
      },
      {
        Variable: "Life Expectancy at Birth",
        Measure: "Years",
        Country: "United States",
        Year: 2010,
        Value: 78.7,
        Component: "Health System Outcomes",
        "Sub-component": "Population Health Status"
      },
      {
        Variable: "Life Expectancy at Birth",
        Measure: "Years",
        Country: "Canada",
        Year: 2010,
        Value: 81.0,
        Component: "Health System Outcomes",
        "Sub-component": "Population Health Status"
      }
    ];

    const variableMap = {};
    dummyData.forEach(row => {
      const component = row.Component || '';
      const subComponent = row['Sub-component'] || '';
      const variable = row.Variable;
      if (!variableMap[component]) {
        variableMap[component] = {};
      }
      if (!variableMap[component][subComponent]) {
        variableMap[component][subComponent] = new Set();
      }
      variableMap[component][subComponent].add(variable);
    });

    Object.keys(variableMap).forEach(component => {
      Object.keys(variableMap[component]).forEach(subComponent => {
        variableMap[component][subComponent] = Array.from(variableMap[component][subComponent]); // Remove .sort()
      });
    });

    const structure = {
      variables: [...new Set(dummyData.map(row => row.Variable))].filter(v => v).sort(),
      measures: [...new Set(dummyData.map(row => row.Measure))].filter(m => m).sort(),
      countries: [...new Set(dummyData.map(row => row.Country))].filter(c => c).sort(),
      years: [...new Set(dummyData.map(row => row.Year))].filter(y => y && Number.isInteger(y) && y >= 2010 && y <= 2030).sort((a, b) => a - b),
      components: [...new Set(dummyData.map(row => row.Component))].filter(c => c).sort(),
      subComponents: [...new Set(dummyData.map(row => row['Sub-component']))].filter(s => s).sort(),
      variableMap
    };

    setData(dummyData);
    setDataStructure(structure);
    setAvailableVariables(structure.variables);
    setAvailableSubComponents(structure.subComponents);
    setAvailableVariables2(structure.variables);
    setAvailableSubComponents2(structure.subComponents);
    setLegendMap({});
    setLoading(false);
  };

  // Enhanced default mappings with United States robustness (moved before useEffect)
  const defaultCountryMapping = {
    "United States": "USA",
    "United States of America": "USA",
    "United Kingdom": "GBR",
    "France": "FRA",
    "Germany": "DEU",
    "Japan": "JPN",
    "China": "CHN",
    "Canada": "CAN",
    "Belgium": "BEL",
    "Denmark": "DNK",
    "Greece": "GRC",
    "Luxembourg": "LUX",
    "Sweden": "SWE",
    "Israel": "ISR",
    "Slovenia": "SVN",
    "Austria": "AUT",
    "Netherlands": "NLD",
    "Norway": "NOR",
    "Finland": "FIN",
    "Spain": "ESP",
    "Italy": "ITA",
    "Portugal": "PRT",
    "Switzerland": "CHE",
    "Ireland": "IRL",
    "Poland": "POL",
    "Czech Republic": "CZE",
    "Hungary": "HUN",
    "Slovakia": "SVK",
    "Croatia": "HRV",
    "Estonia": "EST",
    "Latvia": "LVA",
    "Lithuania": "LTU",
    "Bulgaria": "BGR",
    "Romania": "ROU"
  };

  const defaultM49Mapping = {
    "Belgium": 56,
    "Denmark": 208,
    "Greece": 300,
    "Luxembourg": 442,
    "Sweden": 752,
    "Israel": 376,
    "Slovenia": 705,
    "Germany": 276,
    "France": 250,
    "Japan": 392,
    "Canada": 124,
    "United States": 840,
    "United States of America": 840,
    "Italy": 380,
    "Spain": 724,
    "United Kingdom": 826,
    "Austria": 40,
    "Netherlands": 528,
    "Norway": 578,
    "Finland": 246,
    "Switzerland": 756,
    "Ireland": 372,
    "Poland": 616,
    "Czech Republic": 203,
    "Hungary": 348,
    "Slovakia": 703,
    "Estonia": 233,
    "Latvia": 428,
    "Lithuania": 440,
    "Croatia": 191,
    "Bulgaria": 100,
    "Romania": 642,
    "Australia": 36,
    "Mexico": 484,
    "New Zealand": 554,
    "Turkey": 792,
    "Türkiye": 792,
    "Chile": 152,
    "Colombia": 170,
    "Costa Rica": 188,
    "Indonesia": 360,
    "South Africa": 710,
  };

  // Load data useEffect
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        addDebugInfo('init', 'Starting data loading process');

        try {
          addDebugInfo('mapping', 'Loading country M49 codes');
          const m49Response = await fetch('/country_m49_codes.csv');
          if (!m49Response.ok) {
            throw new Error(`HTTP error fetching M49 codes: ${m49Response.status}`);
          }
          const m49Text = await m49Response.text();
          Papa.parse(m49Text, {
            header: true,
            skipEmptyLines: true,
            complete: results => {
              const codeMapping = {};
              results.data.forEach(row => {
                if (row.Country && row.M49_Code) {
                  codeMapping[row.Country] = row.M49_Code;
                }
              });
              addDebugInfo('mapping', `Loaded ${Object.keys(codeMapping).length} M49 country mappings`, Object.entries(codeMapping).slice(0, 10));
              setM49Mapping(codeMapping);
            },
            error: error => {
              console.error('Error parsing M49 codes CSV:', error);
              setM49Mapping(defaultM49Mapping);
            }
          });
        } catch (error) {
          console.error('Error loading M49 mapping:', error);
          setM49Mapping(defaultM49Mapping);
        }

        try {
          addDebugInfo('mapping', 'Loading country ISO codes');
          const countryCodeResponse = await fetch('/country_iso_codes.csv');
          if (!countryCodeResponse.ok) {
            throw new Error(`HTTP error fetching country codes: ${countryCodeResponse.status}`);
          }
          const countryCodeText = await countryCodeResponse.text();
          Papa.parse(countryCodeText, {
            header: true,
            skipEmptyLines: true,
            complete: results => {
              const codeMapping = {};
              results.data.forEach(row => {
                if (row.Country && row.ISO3) {
                  codeMapping[row.Country] = row.ISO3;
                }
              });
              addDebugInfo('mapping', `Loaded ${Object.keys(codeMapping).length} country mappings`, Object.entries(codeMapping).slice(0, 10));
              setCountryMapping(codeMapping);
            },
            error: error => {
              console.error('Error parsing country codes CSV:', error);
              setCountryMapping(defaultCountryMapping);
            }
          });
        } catch (error) {
          console.error('Error loading country mapping:', error);
          setCountryMapping(defaultCountryMapping);
        }

        try {
          addDebugInfo('map', 'Fetching Natural Earth map data');
          const worldMapResponse = await fetch('/ne_110m_admin_0_countries.json');
          if (!worldMapResponse.ok) {
            throw new Error(`HTTP error fetching Natural Earth map: ${worldMapResponse.status}`);
          }
          const worldMapData = await worldMapResponse.json();
          addDebugInfo('map', 'Natural Earth map data received', {
            type: worldMapData.type,
            objectKeys: worldMapData.objects ? Object.keys(worldMapData.objects) : 'No objects property'
          });
          if (!worldMapData.objects) {
            throw new Error("Invalid TopoJSON: missing 'objects' property");
          }
          const objectKey = 'ne_110m_admin_0_countries';
          if (!worldMapData.objects[objectKey]) {
            throw new Error(`Expected object key '${objectKey}' not found in TopoJSON`);
          }
          addDebugInfo('map', `Using '${objectKey}' for feature extraction`);
          const worldFeatures = feature(worldMapData, worldMapData.objects[objectKey]);
          addDebugInfo('map', 'Feature extraction successful', {
            featuresCount: worldFeatures.features.length
          });
          if (worldFeatures.features.length > 0) {
            const firstFeature = worldFeatures.features[0];
            addDebugInfo('map', 'First feature properties', firstFeature.properties);
          }
          debugRef.current.mapLoaded = true;
          setWorldGeoJSON(worldFeatures);
        } catch (error) {
          console.error('Error loading Natural Earth map data:', error);
          debugRef.current.errors.push({
            type: 'map',
            message: error.message,
            timestamp: new Date().toISOString()
          });
        }

        try {
          addDebugInfo('csv', 'Fetching OECD data index');
          const indexResponse = await fetch(`/Current/OECD_Index.json?v=${Date.now()}`);
          if (!indexResponse.ok) {
            throw new Error(`HTTP error fetching data index: ${indexResponse.status}`);
          }
          const index = await indexResponse.json();
          if (!Array.isArray(index.variables) || index.variables.length === 0) {
            throw new Error('Data index contains no variables');
          }

          const structure = {
            variables: index.variables,
            measures: index.measures,
            countries: index.countries,
            years: index.years,
            components: index.components,
            subComponents: index.subComponents,
            variableMap: index.variableMap
          };

          dataFilesRef.current = index.files || {};
          dataVersionRef.current = index.generatedAt || '';
          loadedVariablesRef.current = new Set();
          debugRef.current.variableMeasures = index.variableMeasures || {};
          debugRef.current.csvLoaded = true;

          addDebugInfo('csv', 'Data index loaded', {
            rows: index.rowCount,
            variables: structure.variables.length,
            measures: structure.measures.length,
            countries: structure.countries.length,
            years: structure.years.length,
            components: structure.components,
            files: Object.keys(dataFilesRef.current).length
          });

          setData([]);
          setDataStructure(structure);
          setAvailableVariables(structure.variables);
          setAvailableSubComponents(structure.subComponents);
          setAvailableVariables2(structure.variables);
          setAvailableSubComponents2(structure.subComponents);
        } catch (error) {
          console.error('Error loading data index:', error);
          debugRef.current.errors.push({
            type: 'csv',
            message: error.message,
            timestamp: new Date().toISOString()
          });
          setDummyData();
        }
        try {
          addDebugInfo('csv', 'Fetching OECD dictionary for variable definitions');
          const dictionaryResponse = await fetch(`/Current/OECD_Dictionary.csv?v=${Date.now()}`);
          if (!dictionaryResponse.ok) {
            throw new Error(`HTTP error fetching dictionary CSV: ${dictionaryResponse.status}`);
          }
          const dictionaryText = await dictionaryResponse.text();

          addDebugInfo('csv', 'Dictionary CSV data received', {
            size: dictionaryText.length,
            sampleLines: dictionaryText.slice(0, 200)
          });
          
          Papa.parse(dictionaryText, {
            header: true,
            skipEmptyLines: true,
            encoding: 'utf-8',
            complete: results => {
              console.log('Dictionary parsing results:', {
                dataLength: results.data.length,
                fields: results.meta.fields,
                sample: results.data.slice(0, 2)
              });
              
              if (results.errors.length > 0) {
                console.warn('Dictionary parsing errors:', results.errors);
              }
              
              const cleanedDictionary = results.data.filter(row => row && row.Variable && row['Variable Definition']);
              const varDefs = {};
              cleanedDictionary.forEach(row => {
                const normalizedVariable = row.Variable.trim();
                varDefs[normalizedVariable] = row['Variable Definition'].trim() || 'Definition not provided';
              });
              
              console.log('Extracted definitions from dictionary:', {
                count: Object.keys(varDefs).length,
                sample: Object.entries(varDefs).slice(0, 3)
              });
              
              addDebugInfo('csv', `Loaded ${Object.keys(varDefs).length} variable definitions from dictionary`);
              setVariableDefinitions(varDefs);
            },
            error: error => {
              console.error('Error parsing dictionary CSV:', error);
              debugRef.current.errors.push({
                type: 'csv_dictionary',
                message: error.message,
                timestamp: new Date().toISOString()
              });
              setVariableDefinitions({}); // Fallback to empty
            }
          });
        } catch (error) {
          console.error('Error loading dictionary CSV:', error);
          debugRef.current.errors.push({
            type: 'csv_dictionary',
            message: error.message,
            timestamp: new Date().toISOString()
          });
          setVariableDefinitions({}); // Fallback to empty
        }
        try {
          addDebugInfo('csv', 'Fetching Legend data');
          const legendResponse = await fetch(`/Current/Legend.csv?v=${Date.now()}`);
          if (!legendResponse.ok) {
            throw new Error(`HTTP error fetching Legend CSV: ${legendResponse.status}`);
          }
          const legendText = await legendResponse.text();
          addDebugInfo('csv', 'Legend CSV data received', {
            size: legendText.length,
            sampleLines: legendText.slice(0, 200)
          });
          Papa.parse(legendText, {
            header: true,
            dynamicTyping: true,
            skipEmptyLines: true,
            complete: results => {
              const legendMap = {};
              results.data.forEach(row => {
                if (row.Variable && row['Unit of Measure'] && row.Binary !== undefined) {
                  const key = `${row.Variable.trim()}-${row['Unit of Measure'].trim()}`;
                  legendMap[key] = row.Binary;
                }
              });
              addDebugInfo('csv', `Loaded ${Object.keys(legendMap).length} legend entries`);
              setLegendMap(legendMap);
            },
            error: error => {
              console.error('Error parsing Legend CSV:', error);
              debugRef.current.errors.push({
                type: 'csv_legend',
                message: error.message,
                timestamp: new Date().toISOString()
              });
            }
          });
        } catch (error) {
          console.error('Error loading Legend CSV:', error);
          debugRef.current.errors.push({
            type: 'csv_legend',
            message: error.message,
            timestamp: new Date().toISOString()
          });
        }

        // FIXED: Enhanced country groupings loading from OECD_Groupings.csv
        try {
          addDebugInfo('csv', 'Fetching OECD groupings data');
          const groupingsResponse = await fetch(`/Current/OECD_Groupings.csv?v=${Date.now()}`);
          if (!groupingsResponse.ok) {
            throw new Error(`HTTP error fetching groupings CSV: ${groupingsResponse.status}`);
          }
          const groupingsText = await groupingsResponse.text();
          
          // Check if we got HTML instead of CSV
          if (groupingsText.startsWith('<!DOCTYPE html>')) {
            throw new Error('Received HTML instead of CSV - file not found?');
          }
          
          addDebugInfo('csv', 'Groupings CSV data received', {
            size: groupingsText.length,
            sampleLines: groupingsText.slice(0, 200)
          });
          
          Papa.parse(groupingsText, {
            header: true,
            skipEmptyLines: true,
            encoding: 'utf-8',
            complete: results => {
              const groupingsData = {};
              const headers = results.meta.fields || [];
              
              // Filter out unwanted columns
              const validHeaders = headers.filter(key => 
                key && 
                key.trim() && 
                key !== 'HSV Countries' && 
                key !== '__parsed_extra'
              );
              
              results.data.forEach(row => {
                validHeaders.forEach(key => {
                  const country = row[key];
                  if (country && 
                      typeof country === 'string' && 
                      country.trim() && 
                      !country.startsWith('These countries') && 
                      !country.startsWith('There is no')) {
                    if (!groupingsData[key]) {
                      groupingsData[key] = [];
                    }
                    groupingsData[key].push(country.trim());
                  }
                });
              });
              
              // Remove duplicates and sort
              Object.keys(groupingsData).forEach(key => {
                groupingsData[key] = [...new Set(groupingsData[key])].sort();
              });
              
              addDebugInfo('csv', `Loaded ${Object.keys(groupingsData).length} groupings`, 
                Object.entries(groupingsData).slice(0, 5).map(([k, v]) => ({
                  group: k,
                  countryCount: v.length,
                  sampleCountries: v.slice(0, 3)
                }))
              );
              setGroupings(groupingsData);
            },
            error: error => {
              console.error('Error parsing groupings CSV:', error);
              debugRef.current.errors.push({
                type: 'csv_groupings',
                message: error.message,
                timestamp: new Date().toISOString()
              });
              setGroupings({});
            }
          });
        } catch (error) {
          console.error('Error loading groupings CSV:', error);
          debugRef.current.errors.push({
            type: 'csv_groupings',
            message: error.message,
            timestamp: new Date().toISOString()
          });
          setGroupings({});
        }

        
        try {
          addDebugInfo('csv', 'Fetching OECD metadata');
          const metadataResponse = await fetch(`/Current/MetaData.csv?v=${Date.now()}`);
          if (!metadataResponse.ok) {
            throw new Error(`HTTP error fetching metadata CSV: ${metadataResponse.status}`);
          }
          const metadataText = await metadataResponse.text();
          
          // Check if we got HTML instead of CSV
          if (metadataText.trim().startsWith('<!DOCTYPE') || metadataText.trim().startsWith('<html')) {
            throw new Error('Received HTML instead of CSV - metadata file not found');
          }
          
          addDebugInfo('csv', 'Metadata CSV data received', {
            size: metadataText.length,
            sampleLines: metadataText.slice(0, 200)
          });
          
          // Use Papa Parse with proper configuration for handling quoted fields with commas
          Papa.parse(metadataText, {
            header: true,
            skipEmptyLines: true,
            encoding: 'utf-8',
            // IMPORTANT: These settings handle commas within quoted fields properly
            quoteChar: '"',
            escapeChar: '"',
            delimiter: ',',
            newline: '\n',
            complete: results => {
              console.log('Metadata parsing results:', {
                dataLength: results.data.length,
                errors: results.errors,
                fields: results.meta.fields,
                sampleData: results.data.slice(0, 2)
              });

              if (results.errors.length > 0) {
                console.warn('Metadata parsing errors:', results.errors);
                // Log first few errors to understand issues
                results.errors.slice(0, 3).forEach((error, index) => {
                  console.warn(`Error ${index + 1}:`, error);
                });
              }

              // Clean and validate the metadata
              const cleanedMetadata = results.data.filter(row => {
                // Keep rows that have at least a Variable field
                return row && row.Variable && typeof row.Variable === 'string' && row.Variable.trim();
              }).map(row => {
                // Clean up the row data - handle field name variations
                const cleanRow = {};
                Object.keys(row).forEach(key => {
                  // Normalize the key name by trimming spaces and handling variations
                  let cleanKey = key.trim();
                  
                  // Handle specific field name variations from your CSV
                  if (cleanKey === 'System Components') cleanKey = 'Component';
                  if (cleanKey === 'Sub-Components') cleanKey = 'Sub-component';
                  if (cleanKey === 'Unit of Measure') cleanKey = 'Measure';
                  
                  // Clean the value - remove extra quotes if present
                  let cleanValue = row[key] ? row[key].toString().trim() : '';
                  
                  // Remove surrounding quotes that might be leftover from CSV parsing
                  if (cleanValue.startsWith('"') && cleanValue.endsWith('"')) {
                    cleanValue = cleanValue.slice(1, -1);
                  }
                  
                  cleanRow[cleanKey] = cleanValue;
                });
                return cleanRow;
              });

              console.log('Cleaned metadata:', {
                originalLength: results.data.length,
                cleanedLength: cleanedMetadata.length,
                sampleClean: cleanedMetadata[0],
                allFields: cleanedMetadata.length > 0 ? Object.keys(cleanedMetadata[0]) : []
              });

              // Check for the specific variable we're testing
              const testVariable = 'Hospital Stay for Acute Myocardial Infarction';
              const testMatch = cleanedMetadata.find(row => row.Variable === testVariable);
              console.log('Test variable lookup for "' + testVariable + '":', testMatch);

              // Extract variable definitions from metadata
              const variableDefinitionsFromMetadata = {};
              cleanedMetadata.forEach(row => {
                if (row.Variable && row['Variable Definition']) {
                  const normalizedVariable = row.Variable.trim();
                  variableDefinitionsFromMetadata[normalizedVariable] = row['Variable Definition'].trim() || 'Definition not provided';
                }
              });
              
              console.log('Variable definitions extracted from metadata:', {
                count: Object.keys(variableDefinitionsFromMetadata).length,
                sampleDefinitions: Object.entries(variableDefinitionsFromMetadata).slice(0, 3),
                testVariableDefinition: variableDefinitionsFromMetadata[testVariable]
              });

              addDebugInfo('csv', `Loaded ${cleanedMetadata.length} metadata entries and ${Object.keys(variableDefinitionsFromMetadata).length} variable definitions`);
              
              // IMPORTANT: Actually set the metadata and variable definitions in state
              setMetadata(cleanedMetadata);
              
              // Merge definitions from dictionary and metadata, with metadata taking precedence
              setVariableDefinitions(prevDefs => ({
                ...prevDefs,
                ...variableDefinitionsFromMetadata
              }));

              // Additional debug: log all available variables in metadata
              const allVariables = cleanedMetadata.map(row => row.Variable).filter(v => v);
              console.log('All variables in metadata:', allVariables.slice(0, 20)); // First 20
            },
            error: error => {
              console.error('Error parsing metadata CSV:', error);
              debugRef.current.errors.push({
                type: 'csv_metadata',
                message: error.message,
                timestamp: new Date().toISOString()
              });
              // Keep existing definitions from dictionary if metadata fails
              setMetadata([]);
            }
          });
        } catch (error) {
          console.error('Error loading metadata CSV:', error);
          debugRef.current.errors.push({
            type: 'csv_metadata',
            message: error.message,
            timestamp: new Date().toISOString()
          });
          // Keep existing definitions from dictionary if metadata fails
          setMetadata([]);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error in data loading process:', error);
        debugRef.current.errors.push({
          type: 'general',
          message: error.message,
          timestamp: new Date().toISOString()
        });
        setDummyData();
      }
    };

    loadData();
  }, []);

// Set default selections after data is loaded
useEffect(() => {
  if (loading || !dataStructure.components.length || !dataStructure.variableMap) return;
  
  addDebugInfo('init', 'Setting default selections', {
    components: dataStructure.components.length,
    variableMapKeys: Object.keys(dataStructure.variableMap)
  });

  // === FIRST VARIABLE DEFAULTS (Population) ===
  
  // Set default Component (First Variable)
  const defaultComponent = dataStructure.components.includes("Health System Context")
    ? "Health System Context"
    : dataStructure.components[0] || '';
  if (defaultComponent) {
    setSelectedComponent(defaultComponent);
    addDebugInfo('ui', `Set default Component: ${defaultComponent}`);
  }

  // Set default Sub-component (First Variable)
  const subComponents = defaultComponent && dataStructure.variableMap[defaultComponent]
    ? Object.keys(dataStructure.variableMap[defaultComponent]).sort()
    : [];
  const defaultSubComponent = subComponents.includes("Demographic")
    ? "Demographic"
    : subComponents[0] || '';
  if (defaultSubComponent) {
    setSelectedSubComponent(defaultSubComponent);
    addDebugInfo('ui', `Set default Sub-component: ${defaultSubComponent}`);
  }

  // Set default Variable (First Variable) - Population
  const variables = defaultComponent && defaultSubComponent && dataStructure.variableMap[defaultComponent]?.[defaultSubComponent]
    ? dataStructure.variableMap[defaultComponent][defaultSubComponent]
    : [];
  const defaultVariable = ["Population All Age Groups - Both Sex", "Total Population"].find(v => variables.includes(v))
    || variables[0] || '';
  if (defaultVariable) {
    setSelectedVariable(defaultVariable);
    addDebugInfo('ui', `Set default Variable: ${defaultVariable}`);
  }

  // Set default Measure (First Variable)
  const measures = defaultVariable && debugRef.current.variableMeasures[defaultVariable]
    ? debugRef.current.variableMeasures[defaultVariable]
    : [];
  const defaultMeasure = measures.includes("Number of Persons")
    ? "Number of Persons"
    : measures[0] || '';
  if (defaultMeasure) {
    setSelectedMeasure(defaultMeasure);
    addDebugInfo('ui', `Set default Measure: ${defaultMeasure}`);
  }

  // === SECOND VARIABLE DEFAULTS (Life Expectancy) ===
  
  // Set default Component (Second Variable) - Health System Outcomes
  const defaultComponent2 = dataStructure.components.includes("Health System Outcomes")
    ? "Health System Outcomes"
    : dataStructure.components[0] || '';
  if (defaultComponent2) {
    setSelectedComponent2(defaultComponent2);
    addDebugInfo('ui', `Set default Component2: ${defaultComponent2}`);
  }

  // Set default Sub-component (Second Variable) - Health (not Population Health Status)
  const subComponents2 = defaultComponent2 && dataStructure.variableMap[defaultComponent2]
    ? Object.keys(dataStructure.variableMap[defaultComponent2]).sort()
    : [];
  const defaultSubComponent2 = subComponents2.includes("Health")
    ? "Health"
    : subComponents2.includes("Population Health Status")
    ? "Population Health Status"
    : subComponents2[0] || '';
  if (defaultSubComponent2) {
    setSelectedSubComponent2(defaultSubComponent2);
    addDebugInfo('ui', `Set default Sub-component2: ${defaultSubComponent2}`);
  }

  // Set default Variable (Second Variable) - Life Expectancy at Birth
  const variables2 = defaultComponent2 && defaultSubComponent2 && dataStructure.variableMap[defaultComponent2]?.[defaultSubComponent2]
    ? dataStructure.variableMap[defaultComponent2][defaultSubComponent2]
    : [];
  
  // Try multiple possible names for life expectancy variable
  let defaultVariable2 = '';
  const lifeExpectancyNames = [
    "Life Expectancy at Birth - Both Sex",
    "Life expectancy at Birth - Total Population",
    "Life Expectancy at Birth",
    "Life expectancy at birth",
    "Life Expectancy"
  ];
  
  for (const name of lifeExpectancyNames) {
    if (variables2.includes(name)) {
      defaultVariable2 = name;
      break;
    }
  }
  
  // Fallback to first available if no life expectancy found
  if (!defaultVariable2 && variables2.length > 0) {
    defaultVariable2 = variables2[0];
  }
  
  if (defaultVariable2) {
    setSelectedVariable2(defaultVariable2);
    addDebugInfo('ui', `Set default Variable2: ${defaultVariable2}`);
  }

  // Set default Measure (Second Variable)
  const measures2 = defaultVariable2 && debugRef.current.variableMeasures[defaultVariable2]
    ? debugRef.current.variableMeasures[defaultVariable2]
    : [];
  const defaultMeasure2 = measures2.includes("Years")
    ? "Years"
    : measures2[0] || '';
  if (defaultMeasure2) {
    setSelectedMeasure2(defaultMeasure2);
    addDebugInfo('ui', `Set default Measure2: ${defaultMeasure2}`);
  }

  // Set default Countries
  setSelectedCountries(dataStructure.countries);
  setSelectedCountries2(dataStructure.countries);
  addDebugInfo('ui', `Set default Countries: All (${dataStructure.countries.length} countries)`);

  // Set default Year
  if (dataStructure.years.length > 0) {
    const defaultYear = dataStructure.years.includes(2010)
      ? 2010
      : dataStructure.years[0];
    setSelectedYear(defaultYear);
    addDebugInfo('ui', `Set default Year: ${defaultYear}`);
  }

}, [loading, dataStructure]);

  // Update sub-components
  useEffect(() => {
    if (!dataStructure.variableMap || !selectedComponent) return;

    const component = selectedComponent || '';
    const subComponents = component && dataStructure.variableMap[component]
      ? Object.keys(dataStructure.variableMap[component]).sort()
      : [];

    setAvailableSubComponents(subComponents);

    if (subComponents.length > 0 && !subComponents.includes(selectedSubComponent)) {
      setSelectedSubComponent(subComponents[0]);
      addDebugInfo('ui', `Set Sub-component to first available: ${subComponents[0]}`);
    } else if (subComponents.length === 0) {
      setSelectedSubComponent('');
      addDebugInfo('ui', 'No sub-components available, cleared selection');
    }

    addDebugInfo('ui', `Updated sub-components for Component: ${component}`, subComponents);
  }, [selectedComponent, selectedSubComponent, dataStructure.variableMap]);

  // Update sub-components for second selection
  useEffect(() => {
    if (!dataStructure.variableMap || !selectedComponent2) return;

    const component = selectedComponent2 || '';
    const subComponents = component && dataStructure.variableMap[component]
      ? Object.keys(dataStructure.variableMap[component]).sort()
      : [];

    setAvailableSubComponents2(subComponents);

    if (subComponents.length > 0 && !subComponents.includes(selectedSubComponent2)) {
      setSelectedSubComponent2(subComponents[0]);
      addDebugInfo('ui', `Set Sub-component2 to first available: ${subComponents[0]}`);
    } else if (subComponents.length === 0) {
      setSelectedSubComponent2('');
      addDebugInfo('ui', 'No sub-components2 available, cleared selection');
    }

    addDebugInfo('ui', `Updated sub-components2 for Component: ${component}`, subComponents);
  }, [selectedComponent2, selectedSubComponent2, dataStructure.variableMap]);

  // Update variables
  useEffect(() => {
    if (!dataStructure.variableMap || !selectedComponent || !selectedSubComponent) return;

    const component = selectedComponent || '';
    const subComponent = selectedSubComponent || '';
    const variables = component && subComponent && dataStructure.variableMap[component]?.[subComponent]
      ? [...dataStructure.variableMap[component][subComponent]] // Remove .sort()
      : [];

    setAvailableVariables(variables);

    if (variables.length > 0 && (!selectedVariable || !variables.includes(selectedVariable))) {
      setSelectedVariable(variables[0]);
      addDebugInfo('ui', `Set Variable to first available: ${variables[0]}`);
    } else if (variables.length === 0) {
      setSelectedVariable('');
      addDebugInfo('ui', 'No variables available, cleared selection');
    }

    addDebugInfo('ui', `Updated variables for Component: ${component}, Sub-component: ${subComponent}`, variables);
  }, [selectedComponent, selectedSubComponent, selectedVariable, dataStructure.variableMap]);

  // Update variables for second selection
  useEffect(() => {
    if (!dataStructure.variableMap || !selectedComponent2 || !selectedSubComponent2) return;

    const component = selectedComponent2 || '';
    const subComponent = selectedSubComponent2 || '';
    const variables = component && subComponent && dataStructure.variableMap[component]?.[subComponent]
      ? [...dataStructure.variableMap[component][subComponent]] // Remove .sort()
      : [];

    setAvailableVariables2(variables);

    if (variables.length > 0 && (!selectedVariable2 || !variables.includes(selectedVariable2))) {
      setSelectedVariable2(variables[0]);
      addDebugInfo('ui', `Set Variable2 to first available: ${variables[0]}`);
    } else if (variables.length === 0) {
      setSelectedVariable2('');
      addDebugInfo('ui', 'No variables2 available, cleared selection');
    }

    addDebugInfo('ui', `Updated variables2 for Component: ${component}, Sub-component: ${subComponent}`, variables);
  }, [selectedComponent2, selectedSubComponent2, selectedVariable2, dataStructure.variableMap]);
  // Update header tooltip content when variables or definitions change
  useEffect(() => {
    const headerTooltipElement = document.querySelector('[data-tooltip-id="header-variable-tooltip"]');
    if (headerTooltipElement && selectedVariable && variableDefinitions) {
      const definition = getVariableDefinition(selectedVariable, variableDefinitions);
      headerTooltipElement.setAttribute('data-tooltip-content', definition);
      console.log('Updated header tooltip content for:', selectedVariable, 'with definition:', definition);
    }
  }, [selectedVariable, selectedVariable2, variableDefinitions, viewMode, activeTab]);
  
  // Fetch one variable's CSV on demand and append its rows to `data`
  const loadVariableData = async (variable) => {
    if (!variable || loadedVariablesRef.current.has(variable)) return;
    const file = dataFilesRef.current[variable];
    if (!file) {
      addDebugInfo('csv', `No data file listed for variable "${variable}"`);
      return;
    }
    loadedVariablesRef.current.add(variable);
    setLoadingVariables(n => n + 1);
    try {
      const response = await fetch(`/Current/${file}?v=${dataVersionRef.current}`);
      if (!response.ok) {
        throw new Error(`HTTP error fetching ${file}: ${response.status}`);
      }
      const text = await response.text();
      const results = Papa.parse(text, {
        header: true,
        dynamicTyping: false,
        skipEmptyLines: true,
        delimiter: ',',
        quoteChar: '"',
        escapeChar: '"'
      });
      if (results.errors.length > 0) {
        console.warn(`Parsing warnings for ${file}:`, results.errors.slice(0, 5));
      }
      const rows = cleanDataRows(results.data);
      addDebugInfo('csv', `Loaded ${rows.length} rows for "${variable}" from ${file}`);
      setData(prev => prev.concat(rows));
    } catch (error) {
      loadedVariablesRef.current.delete(variable);
      console.error('Error loading variable data:', error);
      debugRef.current.errors.push({
        type: 'csv_variable',
        message: error.message,
        timestamp: new Date().toISOString()
      });
    } finally {
      setLoadingVariables(n => n - 1);
    }
  };

  useEffect(() => {
    loadVariableData(selectedVariable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariable, dataStructure]);

  useEffect(() => {
    if (viewMode !== 'single') loadVariableData(selectedVariable2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariable2, viewMode, dataStructure]);

  // Update measures
  // FIXED - removed selectedMeasure from deps and added proper state tracking
  useEffect(() => {
    console.log('🔧 MEASURE UPDATE EFFECT:', {
      selectedVariable,
      debugRefExists: !!debugRef.current.variableMeasures,
      currentMeasure: selectedMeasure,
      timestamp: Date.now()
    });

    if (!selectedVariable || !debugRef.current.variableMeasures) {
      console.log('🔧 Early return: no variable or measures');
      return;
    }

    const measures = debugRef.current.variableMeasures[selectedVariable] || [];
    console.log('🔧 Available measures for', selectedVariable, ':', measures);
    
    setAvailableMeasures(measures);

    // Only update selectedMeasure if it's invalid for current variable
    if (measures.length > 0) {
      if (!selectedMeasure || !measures.includes(selectedMeasure)) {
        console.log('🔧 Setting measure to first available:', measures[0]);
        setSelectedMeasure(measures[0]);
        addDebugInfo('ui', `Set Measure to first available: ${measures[0]}`);
      } else {
        console.log('🔧 Keeping current measure:', selectedMeasure);
      }
    } else {
      console.log('🔧 No measures available, clearing selection');
      setSelectedMeasure('');
      addDebugInfo('ui', 'No measures available, cleared selection');
    }
  }, [selectedVariable]); // FIXED: Only selectedVariable in deps

  // Fix the second variable's measure effect too
  useEffect(() => {
    if (!selectedVariable2 || !debugRef.current.variableMeasures) return;

    const measures = debugRef.current.variableMeasures[selectedVariable2] || [];
    setAvailableMeasures2(measures);

    if (measures.length > 0) {
      if (!selectedMeasure2 || !measures.includes(selectedMeasure2)) {
        setSelectedMeasure2(measures[0]);
        addDebugInfo('ui', `Set Measure2 to first available: ${measures[0]}`);
      }
    } else {
      setSelectedMeasure2('');
      addDebugInfo('ui', 'No measures2 available, cleared selection');
    }
  }, [selectedVariable2]); // FIXED: Only selectedVariable2 in deps

  // Added this new effect to debug measure changes(Caleb)
  useEffect(() => {
    console.log('🔍 MEASURE VALIDATION:', {
      selectedVariable,
      selectedMeasure,
      availableMeasures: availableMeasures.length,
      isValidMeasure: availableMeasures.includes(selectedMeasure),
      debugMeasures: debugRef.current.variableMeasures?.[selectedVariable] || 'NOT FOUND',
      timestamp: Date.now()
    });
    
    // Validate that current measure is actually available
    if (selectedVariable && selectedMeasure && availableMeasures.length > 0) {
      if (!availableMeasures.includes(selectedMeasure)) {
        console.error('🚨 INVALID MEASURE DETECTED:', {
          selectedMeasure,
          availableMeasures,
          selectedVariable
        });
      }
    }
  }, [selectedVariable, selectedMeasure, availableMeasures]);
  // ENHANCED Filter data for first selection with US robustness
  useEffect(() => {
    if (data.length === 0 || !selectedVariable || !selectedMeasure || !selectedComponent || !selectedSubComponent) {
      setFilteredData([]);
      return;
    }

    addDebugInfo('filter', 'Filtering data with criteria', {
      selectedVariable,
      selectedMeasure,
      selectedCountries,
      selectedComponent,
      selectedSubComponent,
      selectedYear
    });

    const validVariable = selectedVariable || '';
    const validMeasure = selectedMeasure || '';
    const validComponent = selectedComponent || '';
    const validSubComponent = selectedSubComponent || '';

    const filtered = data.filter(row => {
      const variableMatch = validVariable === '' || row.Variable === validVariable;
      const measureMatch = validMeasure === '' || row.Measure === validMeasure;
      const countryMatch = selectedCountries.length > 0 && selectedCountries.includes(row.Country);
      const componentMatch = validComponent === '' || row.Component === validComponent;
      const subComponentMatch = validSubComponent === '' || row['Sub-component'] === validSubComponent;
      return variableMatch && measureMatch && countryMatch && componentMatch && subComponentMatch;
    });

    // Enhanced debugging for United States specifically
    const usData = filtered.filter(row => 
      row.Country === 'United States' || 
      row.Country === 'United States of America' || 
      row.Country === 'USA'
    );
    
    if (usData.length > 0) {
      console.log('🇺🇸 United States data found in filtered results:', {
        usDataCount: usData.length,
        years: [...new Set(usData.map(row => row.Year))].sort(),
        sampleValues: usData.slice(0, 3).map(row => ({
          year: row.Year,
          value: row.Value,
          country: row.Country
        }))
      });
    } else if (selectedCountries.includes('United States')) {
      console.log('🔍 No US data found, debugging:', {
        totalFilteredRows: filtered.length,
        selectedCountriesIncludesUS: selectedCountries.includes('United States'),
        allCountriesInData: [...new Set(data.map(row => row.Country))].filter(c => 
          c.includes('United') || c.includes('America') || c.includes('USA')
        ),
        variableMatches: data.filter(row => row.Variable === validVariable).length,
        measureMatches: data.filter(row => row.Measure === validMeasure).length
      });
    }

    addDebugInfo('filter', `Filtered ${filtered.length} rows from ${data.length} total rows`);

    if (filtered.length === 0) {
      const justVariableFilter = data.filter(row => row.Variable === validVariable);
      addDebugInfo('filter', `Variable-only filter returns ${justVariableFilter.length} rows`);
      if (justVariableFilter.length > 0) {
        const measures = [...new Set(justVariableFilter.map(row => row.Measure))];
        addDebugInfo('filter', `Available measures for ${validVariable}`, measures);
      }
    }

    const years = [...new Set(filtered.map(row => row.Year))].filter(y => y && Number.isInteger(y) && y >= 2010 && y <= 2030).sort((a, b) => a - b);
    addDebugInfo('filter', 'Available years in filtered data', years);

    setFilteredData(filtered);
    
    debugRef.current.filterApplied = true;

    // REMOVED THE AUTOMATIC YEAR UPDATING - Let MapView handle it instead
    // This prevents the OECDExplorer from fighting with MapView over year selection

  }, [data, selectedVariable, selectedMeasure, selectedCountries, selectedComponent, selectedSubComponent, countryMapping, m49Mapping]); 
  // REMOVED selectedYear from dependencies
  useEffect(() => {
    // Only validate year after filteredData has been updated
    if (filteredData.length > 0) {
      const availableYearsInData = [...new Set(filteredData.map(row => row.Year))]
        .filter(year => year && Number.isInteger(year))
        .sort((a, b) => a - b);
      
      // Only update if the current year is completely invalid
      if (availableYearsInData.length > 0 && !availableYearsInData.includes(selectedYear)) {
        console.log('Year validation: Current year not in data, updating to first available');
        setSelectedYear(availableYearsInData[0]);
      }
    }
  }, [filteredData]);

  // Filter data for second selection
  useEffect(() => {
    if (data.length === 0 || !selectedVariable2 || !selectedMeasure2 || !selectedComponent2 || !selectedSubComponent2 || viewMode === 'single') {
      setFilteredData2([]);
      return;
    }

    addDebugInfo('filter', 'Filtering data2 with criteria', {
      selectedVariable2,
      selectedMeasure2,
      selectedCountries2,
      selectedComponent2,
      selectedSubComponent2
    });
    // Added this for testing
    window.debugMeasures = () => {
      console.log('=== MEASURE DEBUG ===');
      console.log('Selected Variable:', selectedVariable);
      console.log('Selected Measure:', selectedMeasure);
      console.log('Available Measures:', availableMeasures);
      console.log('Debug Ref Measures for Variable:', debugRef.current.variableMeasures?.[selectedVariable]);
      console.log('Filtered Data Length:', filteredData.length);
      console.log('Sample Filtered Data:', filteredData.slice(0, 3));
    };
    const validVariable = selectedVariable2 || '';
    const validMeasure = selectedMeasure2 || '';
    const validComponent = selectedComponent2 || '';
    const validSubComponent = selectedSubComponent2 || '';

    const filtered = data.filter(row => {
      const variableMatch = validVariable === '' || row.Variable === validVariable;
      const measureMatch = validMeasure === '' || row.Measure === validMeasure;
      const countryMatch = selectedCountries2.length > 0 && selectedCountries2.includes(row.Country); // FIXED
      const componentMatch = validComponent === '' || row.Component === validComponent;
      const subComponentMatch = validSubComponent === '' || row['Sub-component'] === validSubComponent;
      return variableMatch && measureMatch && countryMatch && componentMatch && subComponentMatch;
    });

    addDebugInfo('filter', `Filtered ${filtered.length} rows for data2 from ${data.length} total rows`);

    setFilteredData2(filtered);
  }, [data, selectedVariable2, selectedMeasure2, selectedCountries2, selectedComponent2, selectedSubComponent2, viewMode]);

  // Cleanup animation
  useEffect(() => {
    return () => {
      if (animationInterval.current) {
        clearInterval(animationInterval.current);
        animationInterval.current = null;
      }
    };
  }, []);

  useEffect(() => {
  // Check if font is loaded
  const checkFont = () => {
    if (document.fonts && document.fonts.check) {
      const isFontLoaded = document.fonts.check('300 16px Calibre-Light');
      if (isFontLoaded && !fontLoaded) {
        setFontLoaded(true);
        // Force tooltip re-initialization
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'));
        }, 100);
      }
    }
  };

    checkFont();
    document.fonts?.ready.then(checkFont);
  }, [fontLoaded]);

  // Set activeTab to chart when switching to comparative mode
  useEffect(() => {
    if (viewMode === 'comparative') {
      setActiveTab('chart');
      addDebugInfo('ui', 'Set activeTab to chart due to comparative mode');
    }
  }, [viewMode]);

window.debugOECDExplorer = () => {
    console.log('=== OECDExplorer Debug Info ===');
    console.log('Status:', {
      mapLoaded: debugRef.current.mapLoaded,
      csvLoaded: debugRef.current.csvLoaded,
      filterApplied: debugRef.current.filterApplied,
      errorCount: debugRef.current.errors.length
    });

    console.log('State:', {
      loading,
      dataRows: data.length,
      filteredRows: filteredData.length,
      filteredRows2: filteredData2.length,
      worldGeoJSON: worldGeoJSON ? `${worldGeoJSON.features.length} features` : 'Not loaded',
      countryMappingEntries: Object.keys(countryMapping).length,
      m49MappingEntries: Object.keys(m49Mapping).length,
      availableMeasures: availableMeasures.length,
      availableVariables: availableVariables.length,
      availableSubComponents: availableSubComponents.length,
      activeTab: activeTab,
      viewMode: viewMode,
      mapMode: mapMode,
      selectedYear
    });

    console.log('Selected filters:', {
      variable: selectedVariable,
      measure: selectedMeasure,
      countries: selectedCountries,
      component: selectedComponent,
      subComponent: selectedSubComponent,
      variable2: selectedVariable2,
      measure2: selectedMeasure2,
      countries2: selectedCountries2,
      component2: selectedComponent2,
      subComponent2: selectedSubComponent2,
      chartXAxis,
      chartType,
      showLegend
    });

    if (debugRef.current.variableMeasures && selectedVariable) {
      console.log(`Available measures for ${selectedVariable}:`, debugRef.current.variableMeasures[selectedVariable] || 'None');
    }

    console.log('Full debug log:', debugRef.current);

    const diagnosis = [];

    if (filteredData.length === 0 && viewMode === 'single') {
      diagnosis.push("No data matches the current filter criteria");
      if (selectedVariable && !dataStructure.variables.includes(selectedVariable)) {
        diagnosis.push(`Selected variable "${selectedVariable}" not found in data`);
      }
      if (selectedVariable && selectedMeasure && debugRef.current.variableMeasures && debugRef.current.variableMeasures[selectedVariable] && !debugRef.current.variableMeasures[selectedVariable].includes(selectedMeasure)) {
        diagnosis.push(`Selected measure "${selectedMeasure}" not available for variable "${selectedVariable}"`);
      }
      if (selectedComponent && !dataStructure.components.includes(selectedComponent)) {
        diagnosis.push(`Selected component "${selectedComponent}" not found in data`);
      }
      if (selectedSubComponent && !dataStructure.subComponents.includes(selectedSubComponent)) {
        diagnosis.push(`Selected sub-component "${selectedSubComponent}" not found in data`);
      }
    }

    if (filteredData2.length === 0 && viewMode === 'comparative') {
      diagnosis.push("No data matches the second filter criteria");
      if (selectedVariable2 && !dataStructure.variables.includes(selectedVariable2)) {
        diagnosis.push(`Selected variable2 "${selectedVariable2}" not found in data`);
      }
      if (selectedVariable2 && selectedMeasure2 && debugRef.current.variableMeasures && debugRef.current.variableMeasures[selectedVariable2] && !debugRef.current.variableMeasures[selectedVariable2].includes(selectedMeasure2)) {
        diagnosis.push(`Selected measure2 "${selectedMeasure2}" not available for variable "${selectedVariable2}"`);
      }
      if (selectedComponent2 && !dataStructure.components.includes(selectedComponent2)) {
        diagnosis.push(`Selected component2 "${selectedComponent2}" not found in data`);
      }
      if (selectedSubComponent2 && !dataStructure.subComponents.includes(selectedSubComponent2)) {
        diagnosis.push(`Selected sub-component2 "${selectedSubComponent2}" not found in data`);
      }
    }

    if (diagnosis.length > 0) {
      console.log('Diagnosis:', diagnosis);
    }

    return 'Debug info logged to console';
  };
  //again che asked for unit of measure to not be radio button style
  /*
  const toggleMeasureDropdown = () => setShowMeasureDropdown(!showMeasureDropdown);
  const handleMeasureSelect = measure => {
    setSelectedMeasure(measure);
    setShowMeasureDropdown(false);
    addDebugInfo('ui', `Selected Measure: ${measure}`);
  };
  const toggleMeasureDropdown2 = () => setShowMeasureDropdown2(!showMeasureDropdown2);
  const handleMeasureSelect2 = measure => { 
    setSelectedMeasure2(measure);
    setShowMeasureDropdown2(false);
    addDebugInfo('ui', `Selected Measure2: ${measure}`);
  };
  */
// Fixed handleCountryToggle function
const handleCountryToggle = (opt) => {
  if (opt.type === 'country') {
    // Individual country selection - toggle on/off
    setSelectedCountries(prevSelected => {
      const isSelected = prevSelected.includes(opt.value);
      const newSelected = isSelected 
        ? prevSelected.filter(c => c !== opt.value)
        : [...prevSelected, opt.value];
      addDebugInfo('ui', `${isSelected ? 'Deselected' : 'Selected'} Country: ${opt.value}`);
      return newSelected;
    });
  } else {
    // Group selection - replace current selection with group countries
    setSelectedCountries(prevSelected => {
      const groupCountries = opt.countries;
      const allGroupCountriesSelected = groupCountries.every(c => prevSelected.includes(c));
      const onlyGroupCountriesSelected = prevSelected.length === groupCountries.length && 
                                        allGroupCountriesSelected;
      
      let newSelected;
      
      // If clicking on a group that's already fully selected (and no extra countries), deselect it
      if (onlyGroupCountriesSelected) {
        newSelected = [];
        addDebugInfo('ui', `Deselected Group: ${opt.value} (was only selection)`);
      }
      // If all group countries are selected but there are extra countries, 
      // replace selection with just the group
      else if (allGroupCountriesSelected) {
        newSelected = [...groupCountries];
        addDebugInfo('ui', `Isolated Group: ${opt.value} (removed other countries)`);
      }
      // If not all group countries are selected, replace selection with the group
      else {
        newSelected = [...groupCountries];
        addDebugInfo('ui', `Selected Group: ${opt.value} (replaced previous selection)`);
      }
      
      return newSelected;
    });
  }
};

// Fixed handleCountryToggle2 function for comparative mode
const handleCountryToggle2 = (opt) => {
  if (opt.type === 'country') {
    // Individual country selection - toggle on/off
    setSelectedCountries2(prevSelected => {
      const isSelected = prevSelected.includes(opt.value);
      const newSelected = isSelected 
        ? prevSelected.filter(c => c !== opt.value)
        : [...prevSelected, opt.value];
      addDebugInfo('ui', `${isSelected ? 'Deselected' : 'Selected'} Country2: ${opt.value}`);
      return newSelected;
    });
  } else {
    // Group selection - replace current selection with group countries
    setSelectedCountries2(prevSelected => {
      const groupCountries = opt.countries;
      const allGroupCountriesSelected = groupCountries.every(c => prevSelected.includes(c));
      const onlyGroupCountriesSelected = prevSelected.length === groupCountries.length && 
                                        allGroupCountriesSelected;
      
      let newSelected;
      
      // If clicking on a group that's already fully selected (and no extra countries), deselect it
      if (onlyGroupCountriesSelected) {
        newSelected = [];
        addDebugInfo('ui', `Deselected Group2: ${opt.value} (was only selection)`);
      }
      // If all group countries are selected but there are extra countries, 
      // replace selection with just the group
      else if (allGroupCountriesSelected) {
        newSelected = [...groupCountries];
        addDebugInfo('ui', `Isolated Group2: ${opt.value} (removed other countries)`);
      }
      // If not all group countries are selected, replace selection with the group
      else {
        newSelected = [...groupCountries];
        addDebugInfo('ui', `Selected Group2: ${opt.value} (replaced previous selection)`);
      }
      
      return newSelected;
    });
  }
};
  const handleSelectAllCountries = () => {
    setSelectedCountries(dataStructure.countries);
    addDebugInfo('ui', 'Selected all countries');
  };
  const handleDeselectAllCountries = () => {
    setSelectedCountries([]);
    addDebugInfo('ui', 'Deselected all countries');
  };
  const handleSelectAllCountries2 = () => {
    setSelectedCountries2(dataStructure.countries);
    addDebugInfo('ui', 'Selected all countries2');
  };
  const handleDeselectAllCountries2 = () => {
    setSelectedCountries2([]);
    addDebugInfo('ui', 'Deselected all countries2');
  };

console.log('Rendering OECDExplorer, isSidebarOpen:', isSidebarOpen, 'dataStructure:', {
    components: dataStructure.components.length,
    subComponents: dataStructure.subComponents.length,
    variables: dataStructure.variables.length,
    measures: dataStructure.measures.length
});

if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-message">Loading Health Systems Data...</div>
      </div>
    );
}

// Enhanced sorting logic for data popup table
let tableData;

if (viewMode === 'single') {
  tableData = activeTab === 'map' && selectedYear
    ? filteredData.filter(row => row.Year === selectedYear)
    : filteredData;
} else {
  let data1 = activeTab === 'map' && selectedYear
    ? filteredData.filter(row => row.Year === selectedYear)
    : filteredData;
  let data2 = activeTab === 'map' && selectedYear
    ? filteredData2.filter(row => row.Year === selectedYear)
    : filteredData2;

  const dataMap1 = new Map();
  data1.forEach(row => {
    const key = `${row.Country}-${row.Year}`;
    dataMap1.set(key, row.Value);
  });

  const dataMap2 = new Map();
  data2.forEach(row => {
    const key = `${row.Country}-${row.Year}`;
    dataMap2.set(key, row.Value);
  });

  const uniqueKeys = [...new Set([...dataMap1.keys(), ...dataMap2.keys()])];

  tableData = uniqueKeys.map(key => {
    const [country, year] = key.split('-');
    return {
      Country: country,
      Year: parseInt(year),
      Value1: dataMap1.get(key) ?? null,
      Value2: dataMap2.get(key) ?? null,
      Measure1: selectedMeasure,
      Variable1: selectedVariable,
      Measure2: selectedMeasure2,
      Variable2: selectedVariable2
    };
  });
}

// Enhanced sorting with better type handling and null value management
if (sortConfig.key && tableData.length > 0) {
  tableData = [...tableData].sort((a, b) => {
    let aValue, bValue;
    
    // Handle string sorting (Country, Variable, Measure, Component, Sub-component)
    if (['Country', 'Variable', 'Measure', 'Component', 'Sub-component', 'Variable1', 'Variable2', 'Measure1', 'Measure2'].includes(sortConfig.key)) {
      aValue = a[sortConfig.key] || '';
      bValue = b[sortConfig.key] || '';
      return sortConfig.direction === 'asc' 
        ? aValue.localeCompare(bValue) 
        : bValue.localeCompare(aValue);
    } 
    // Handle Year sorting
    else if (sortConfig.key === 'Year') {
      aValue = a.Year || 0;
      bValue = b.Year || 0;
    } 
    // Handle Value sorting (single mode)
    else if (viewMode === 'single' && sortConfig.key === 'Value') {
      aValue = (typeof a.Value === 'number' && !isNaN(a.Value)) ? a.Value : -Infinity;
      bValue = (typeof b.Value === 'number' && !isNaN(b.Value)) ? b.Value : -Infinity;
    } 
    // Handle Value1 sorting (comparative mode)
    else if (viewMode === 'comparative' && sortConfig.key === 'Value1') {
      aValue = (a.Value1 !== null && typeof a.Value1 === 'number' && !isNaN(a.Value1)) ? a.Value1 : -Infinity;
      bValue = (b.Value1 !== null && typeof b.Value1 === 'number' && !isNaN(b.Value1)) ? b.Value1 : -Infinity;
    } 
    // Handle Value2 sorting (comparative mode)
    else if (viewMode === 'comparative' && sortConfig.key === 'Value2') {
      aValue = (a.Value2 !== null && typeof a.Value2 === 'number' && !isNaN(a.Value2)) ? a.Value2 : -Infinity;
      bValue = (b.Value2 !== null && typeof b.Value2 === 'number' && !isNaN(b.Value2)) ? b.Value2 : -Infinity;
    } 
    // Default case
    else {
      return 0;
    }
    
    // Handle numeric sorting
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    return 0;
  });
}

const maxValue = Math.max(
    ...tableData.map(row => typeof row.Value === 'number' && !isNaN(row.Value) ? row.Value : 0),
    0
);

const hasSelections = selectedVariable && selectedMeasure && selectedComponent && selectedSubComponent;

// Define custom order for components
const componentOrder = ['Health System Context', 'Health System Functions', 'Health System Outputs', 'Health System Objectives', 'Health System Outcomes'];
const sortedComponents = dataStructure.components.length > 0 ? [...dataStructure.components].sort((a, b) => {
    const indexA = componentOrder.indexOf(a);
    const indexB = componentOrder.indexOf(b);
    if (indexA === -1 && indexB === -1) return a.localeCompare(b);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
}) : [];

addDebugInfo('ui', 'Right panel visibility check', {
    hasSelections,
    mapMode,
    tableDataLength: tableData.length,
    selectedYear
});

const InfoIcon = () => <i className="fas fa-circle-info custom-info-icon" />;

return (
    <>
      {showTermsPopup && (
        <TermsAndConditionsPopup onAccept={handleTermsAccept} />
      )}
      
      <div 
        className="explorer-container" 
        data-sidebar-open={isSidebarOpen.toString()}
        style={{ '--sidebar-width': isSidebarOpen ? '300px' : '40px' }}
      >
      <div className={`sidebar ${isSidebarOpen ? 'open' : 'collapsed'}`}>
          <button className="sidebar-toggle" onClick={toggleSidebar}>
            {isSidebarOpen ? '◄' : '►'}
          </button>
          {isSidebarOpen && (
            <div className="sidebar-content">
              {console.log('Rendering sidebar with selections:', { selectedComponent, selectedSubComponent, selectedVariable, selectedMeasure })}
              <div className="section header-section">
                <div className="header-content">
                  <a
                    href="https://hsph.harvard.edu/research/health-systems-innovation-lab/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="app-logo-link"
                  >
                    <img
                      src="/logo.png"
                      alt="Harvard Health Systems Innovation Logo"
                      className="app-logo"
                    />
                  </a>
                  <div className="title-wrapper flex justify-center items-center">
                    <h1 className="app-title text-2xl font-bold ultra-emphasis">Health Systems Visualiser</h1>
                  </div>
                </div>
              </div>
              <div className="tab-selector">
                <button
                  className={`tab-button super-bold-button ${activeTab === 'chart' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab('chart');
                    addDebugInfo('ui', 'Switched to chart view');
                  }}
                >
                  Chart View
                </button>
                <button
                  className={`tab-button super-bold-button ${activeTab === 'map' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab('map');
                    setViewMode('single');
                    addDebugInfo('ui', 'Switched to map view');
                  }}
                  disabled={false}
                >
                  World Map
                </button>
              </div>
              
              {activeTab === 'chart' && (
                <div className="chart-view-selector">
                  <button
                    className={`tab-button super-bold-button ${viewMode === 'single' ? 'active' : ''}`}
                    onClick={() => {
                      setViewMode('single');
                      setActiveTab('chart');
                      addDebugInfo('ui', 'Switched to single chart view');
                    }}
                  >
                    Single
                  </button>
                  <button
                    className={`tab-button super-bold-button ${viewMode === 'comparative' ? 'active' : ''}`}
                    onClick={() => {
                      setViewMode('comparative');
                      setActiveTab('chart');
                      addDebugInfo('ui', 'Switched to comparative chart view');
                    }}
                  >
                    Comparative
                  </button>
                </div>
              )}
              <div className="section">
                <h2 className="section-title" style={{width: '250px', fontWeight: 'normal'}}> Health System Component</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <select
                    className="select-input"
                    value={selectedComponent || ''}
                    onChange={e => {
                      setSelectedComponent(e.target.value);
                      setSelectedSubComponent('');
                      setSelectedVariable('');
                      setSelectedMeasure('');
                      addDebugInfo('ui', `Selected Component: ${e.target.value}`);
                    }}
                    style={{backgroundColor: '#ffffff', color: '#000000', flex: 1}}
                  >
                    {sortedComponents.map(component => (
                      <option key={component} value={component}>
                        {component}
                      </option>
                    ))}
                  </select>
                  <button
                    data-tooltip-id="component-tooltip"
                    data-tooltip-content="The building blocks that comprise a health system, and which are used to characterise and analyse system behaviour and performance."
                    className="text-blue-500 hover:text-blue-700"
                    aria-label="Health System Components information"
                    style={{ flexShrink: 0 }}
                  >
                    <InfoIcon />
                  </button>
                </div>
              </div>

              <div className="section">
                <h2 className="section-title" style={{width: '250px', fontWeight: 'normal'}}> Health System Sub-Component</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <select
                    className="select-input"
                    value={selectedSubComponent}
                    onChange={e => {
                      setSelectedSubComponent(e.target.value);
                      setSelectedVariable('');
                      setSelectedMeasure('');
                      addDebugInfo('ui', `Selected Sub-component: ${e.target.value}`);
                    }}
                    disabled={!selectedComponent}
                    style={{backgroundColor: '#ffffff', color: '#000000', flex: 1}}
                  >
                    {availableSubComponents.map(subComponent => (
                      <option key={subComponent} value={subComponent}>{subComponent}</option>
                    ))}
                  </select>
                  <button
                    data-tooltip-id="subcomponent-tooltip"
                    data-tooltip-content="The more specific elements that comprise each health sytem component."
                    className="text-blue-500 hover:text-blue-700"
                    aria-label="Health System Sub-components information"
                    style={{ flexShrink: 0 }}
                  >
                    <InfoIcon />
                  </button>
                </div>
              </div>

              <div className="section">
                <h2 className="section-title" style={{width: '250px', fontWeight: 'normal'}}> Variable</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <select
                    className="select-input"
                    value={selectedVariable}
                    onChange={e => {
                      const newVariable = e.target.value;
                      setSelectedVariable(newVariable);
                      setSelectedMeasure('');
                      addDebugInfo('ui', `Selected Variable: ${newVariable}`);
                    }}
                    disabled={!selectedSubComponent}
                    style={{backgroundColor: '#ffffff', color: '#000000', flex: 1}}
                  >
                    {availableVariables.map(variable => (
                      <option key={variable} value={variable}>{variable}</option>
                    ))}
                  </select>
                  <button
                    data-tooltip-id="variable-tooltip"
                    data-tooltip-content="A measurable indicator or metric that provides a 'tracer' to analyse a given health system component and/or sub - component."
                    className="text-blue-500 hover:text-blue-700"
                    aria-label="Variables information"
                    style={{ flexShrink: 0 }}
                  >
                    <InfoIcon />
                  </button>
                </div>
              </div>

              <div className="section">
                <h2 className="section-title" style={{width: '250px', fontWeight: 'normal'}}> Unit of Measure</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <select
                    className="select-input"
                    value={selectedMeasure}
                    onChange={e => {
                      setSelectedMeasure(e.target.value);
                      addDebugInfo('ui', `Selected Measure: ${e.target.value}`);
                    }}
                    disabled={!selectedVariable}
                    style={{backgroundColor: '#ffffff', color: '#000000', flex: 1}}
                  >
                    {availableMeasures.map((measure, index) => (
                      <option key={index} value={measure}>
                        {measure}
                      </option>
                    ))}
                  </select>
                  <button
                    data-tooltip-id="measure-tooltip"
                    data-tooltip-content="The units used to measure a given variable of interest."
                    className="text-blue-500 hover:text-blue-700"
                    aria-label="Unit of Measure information"
                    style={{ flexShrink: 0 }}
                  >
                    <InfoIcon />
                  </button>
                </div>
              </div>
              <div className="section">
                <h2 className="section-title" style={{width: '250px'}}>Countries</h2>
                <SearchableMultiSelect
                  options={dataStructure.countries}
                  selected={selectedCountries}
                  onToggle={handleCountryToggle}
                  onSelectAll={handleSelectAllCountries}
                  onDeselectAll={handleDeselectAllCountries}
                  placeholder="Select Countries"
                  id="countries-select"
                  groupings={groupings}
                  totalCountries={dataStructure.countries.length}
                />
              </div>
              {viewMode === 'comparative' && (
                <>
                  <div className="section">
                    <h2 className="section-title" style={{width: '250px'}}>Health System Component 2</h2>
                    <select
                      className="select-input"
                      value={selectedComponent2 || ''}
                      onChange={e => {
                        setSelectedComponent2(e.target.value);
                        setSelectedSubComponent2('');
                        setSelectedVariable2('');
                        setSelectedMeasure2('');
                        addDebugInfo('ui', `Selected Component2: ${e.target.value}`);
                      }}
                      style={{backgroundColor: '#ffffff', color: '#000000'}}
                    >
                      {sortedComponents.map(component => (
                        <option key={component} value={component}>
                          {component}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="section">
                    <h2 className="section-title" style={{width: '250px'}}>Health System Sub-Component 2</h2>
                    <select
                      className="select-input"
                      value={selectedSubComponent2}
                      onChange={e => {
                        setSelectedSubComponent2(e.target.value);
                        setSelectedVariable2('');
                        setSelectedMeasure2('');
                        addDebugInfo('ui', `Selected Sub-component2: ${e.target.value}`);
                      }}
                      disabled={!selectedComponent2}
                      style={{backgroundColor: '#ffffff', color: '#000000'}}
                    >
                      {availableSubComponents2.map(subComponent => (
                        <option key={subComponent} value={subComponent}>{subComponent}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="section">
                    <h2 className="section-title" style={{width: '250px'}}>Variable 2</h2>
                    <select
                      className="select-input"
                      value={selectedVariable2}
                      onChange={e => {
                        const newVariable = e.target.value;
                        setSelectedVariable2(newVariable);
                        setSelectedMeasure2('');
                        addDebugInfo('ui', `Selected Variable2: ${newVariable}`);
                      }}
                      disabled={!selectedSubComponent2}
                      style={{backgroundColor: '#ffffff', color: '#000000'}}
                    >
                      {availableVariables2.map(variable => (
                        <option key={variable} value={variable}>{variable}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="section">
                    <h2 className="section-title" style={{width: '250px'}}>Unit of Measure 2</h2>
                    <select
                      className="select-input"
                      value={selectedMeasure2}
                      onChange={e => {
                        setSelectedMeasure2(e.target.value);
                        addDebugInfo('ui', `Selected Measure2: ${e.target.value}`);
                      }}
                      disabled={!selectedVariable2}
                      style={{backgroundColor: '#ffffff', color: '#000000'}}
                    >
                      {availableMeasures2.map((measure, index) => (
                        <option key={index} value={measure}>
                          {measure}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="section">
                    <h2 className="section-title" style={{width: '250px'}}>Countries 2</h2>
                    <SearchableMultiSelect
                      options={dataStructure.countries}
                      selected={selectedCountries2}
                      onToggle={handleCountryToggle2}
                      onSelectAll={handleSelectAllCountries2}
                      onDeselectAll={handleDeselectAllCountries2}
                      placeholder="Select Countries (Second)"
                      id="countries-select2"
                      groupings={groupings}
                      totalCountries={dataStructure.countries.length}
                    />
                  </div>
                </>
              )}
{/* Updated button section with equidistant Y spacing */}
              <div className="section" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px', // Equal spacing between all buttons
                width: '100%'
              }}>
                <button 
                  className="button button-secondary"
                  onClick={toggleData}
                  title="Show Data Table"
                  style={{
                    marginBottom: '0' // Override any existing margin
                  }}
                >
                  Show Data
                </button>
                <button 
                  className="button button-secondary"
                  onClick={toggleMetadata}
                  title="Show Variable Metadata"
                  style={{
                    marginBottom: '0' // Override any existing margin
                  }}
                >
                  Show Metadata
                </button>
                <button
                  className="button button-secondary"
                  onClick={downloadPrimer}
                  title="Download Health Systems Primer PDF"
                  style={{
                    marginBottom: '0' // Override any existing margin
                  }}
                >
                  Download Primer
                </button>
                <button
                  className="button button-secondary"
                  onClick={downloadHandbook}
                  title="Download Visualizer Handbook PDF"
                  style={{
                    marginBottom: '0' // Override any existing margin
                  }}
                >
                  Download Handbook
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="main-content" style={{flex: 1}}>
          <div className="header">
            {activeTab === 'map' && (
              <div className="inline-flex items-center">
                <h1 className="header-title">{selectedVariable || 'Select a Variable'}</h1>
                {selectedVariable && (
                  <button
                    data-tooltip-id="header-variable-tooltip"
                    data-tooltip-content={getVariableDefinition(selectedVariable, variableDefinitions)}
                    className="text-blue-500 hover:text-blue-700 ml-2"
                    aria-label="Variable definition"
                  >
                    <InfoIcon />
                  </button>
                )}
              </div>
            )}
          </div>
          
          <div className="content-area" style={{width: '100%', height: '100%'}}>
            {/* Your existing chart/map content here */}
            {activeTab === 'map' ? (
              <div className="map-view-wrapper">
                <MapView
                  filteredData={filteredData}
                  countryMapping={countryMapping}
                  m49Mapping={m49Mapping}
                  worldGeoJSON={worldGeoJSON}
                  selectedYear={selectedYear}
                  setSelectedYear={setSelectedYear}
                  mapMode={mapMode}
                  setMapMode={setMapMode}
                  isSidebarOpen={isSidebarOpen}
                  dataStructure={dataStructure}
                  selectedVariable={selectedVariable}
                  selectedMeasure={selectedMeasure}
                  stopAnimation={stopAnimation}
                  legendBinary={legendMap[`${selectedVariable}-${selectedMeasure}`]}
                  legendMap={legendMap}
                  variableDefinitions={variableDefinitions} 
                />
              </div>
            ) : activeTab === 'chart' ? (
              viewMode === 'single' ? (
                <ChartView
                  key={`${selectedVariable}-${selectedMeasure}`} 
                  filteredData={filteredData}
                  selectedVariable={selectedVariable}
                  selectedMeasure={selectedMeasure}
                  dataStructure={dataStructure}
                  chartXAxis={chartXAxis}
                  chartType={chartType}
                  showLegend={showLegend}
                  isSidebarOpen={isSidebarOpen}
                  variableDefinitions={variableDefinitions}
                />
              ) : (
                <ComparativeChartView
                  filteredData={filteredData}
                  filteredData2={filteredData2}
                  selectedVariable={selectedVariable}
                  selectedMeasure={selectedMeasure}
                  selectedVariable2={selectedVariable2}
                  selectedMeasure2={selectedMeasure2}
                  dataStructure={dataStructure}
                  chartXAxis={chartXAxis}
                  showLegend={showLegend}
                  isSidebarOpen={isSidebarOpen}
                  variableDefinitions={variableDefinitions}
                />
              )
            ) : null}
          </div>
        </div>
        </div>
        
        {/* Fixed positioned watermarks */}
        <div className="watermarks-container" style={{
          position: 'fixed',
          bottom: '20px',
          left: isSidebarOpen ? '320px' : '60px',
          right: '20px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pointerEvents: 'none', // Allow clicks to pass through
          zIndex: 1000
        }}>
          {/* Copyright text */}
          <div style={{
            fontSize: '12px',
            color: '#333333',
            fontFamily: 'Arial, sans-serif',
            fontWeight: 'normal',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            padding: '4px 8px',
            borderRadius: '4px',
            backdropFilter: 'blur(4px)'
          }}>
            © Health Systems Innovation Lab, Harvard University, 2025
          </div>
          
          {/* Logo */}
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            padding: '4px',
            borderRadius: '4px',
            backdropFilter: 'blur(4px)'
          }}>
            <img
              src="/logo.png"
              alt="Harvard Health Systems Innovation Logo"
              style={{
                height: '40px',
                width: 'auto',
                opacity: 1
              }}
            />
          </div>
        </div>

      {showMetadata && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem'
          }}
          onClick={toggleMetadata}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '800px',
            maxHeight: '80vh',
            width: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}
          onClick={(e) => e.stopPropagation()}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#1F2937'
              }}>
                Variable Metadata
              </h3>
              <button
                onClick={toggleMetadata}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#6B7280',
                  padding: '0.25rem'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{
              flex: 1,
              overflow: 'auto',
              border: '1px solid #E5E7EB',
              borderRadius: '4px',
              padding: '1rem',
              backgroundColor: '#F9FAFB'
            }}>
                            {(() => {
                const currentMetadata = getCurrentMetadata();
                if (!currentMetadata || (Array.isArray(currentMetadata) && currentMetadata.length === 0)) {
                  return <p style={{ textAlign: 'center', padding: '2rem', color: '#6B7280', fontStyle: 'italic' }}>No metadata found for the current selection.</p>;
                }

                const metadataFields = [
                  { key: 'Source', label: 'Source' },
                  { key: 'Component', label: 'Component' },
                  { key: 'Sub-component', label: 'Sub-component' },
                  { key: 'Variable', label: 'Variable' },
                  { key: 'Variable Definition', label: 'Variable Definition' },
                  { key: 'Measure', label: 'Unit of Measure' },
                  { key: 'Unit Standardization', label: 'Unit Standardization' },
                  { key: 'Date Range', label: 'Date Range' },
                  { key: 'URL', label: 'URL' }
                ];

                if (Array.isArray(currentMetadata)) {
                  return currentMetadata.map((meta, index) => (
                    <div key={index} style={{ marginBottom: '2rem', borderBottom: index < currentMetadata.length - 1 ? '1px solid #E5E7EB' : 'none', paddingBottom: index < currentMetadata.length - 1 ? '1rem' : '0' }}>
                      <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#1F2937', marginBottom: '1rem' }}>Variable {index + 1}: {meta.Variable}</h4>
                      <div style={{ display: 'grid', gap: '1rem' }}>
                        {metadataFields.map(({ key, label }) => (
                          <div key={key} style={{ borderBottom: '1px solid #E5E7EB', paddingBottom: '0.75rem' }}>
                            <dt style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>{label}:</dt>
                            <dd style={{ fontSize: '0.875rem', color: '#1F2937', margin: 0, wordBreak: 'break-word' }}>
                              {key === 'URL' && meta[key] ? (
                                <a href={meta[key]} target="_blank" rel="noopener noreferrer" style={{ color: '#3B82F6', textDecoration: 'underline' }}>{meta[key]}</a>
                              ) : (meta[key] || 'Not specified')}
                            </dd>
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                } else {
                  return (
                    <div style={{ display: 'grid', gap: '1rem' }}>
                      {metadataFields.map(({ key, label }) => (
                        <div key={key} style={{ borderBottom: '1px solid #E5E7EB', paddingBottom: '0.75rem' }}>
                          <dt style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>{label}:</dt>
                          <dd style={{ fontSize: '0.875rem', color: '#1F2937', margin: 0, wordBreak: 'break-word' }}>
                            {key === 'URL' && currentMetadata[key] ? (
                              <a href={currentMetadata[key]} target="_blank" rel="noopener noreferrer" style={{ color: '#3B82F6', textDecoration: 'underline' }}>{currentMetadata[key]}</a>
                            ) : (currentMetadata[key] || 'Not specified')}
                          </dd>
                        </div>
                      ))}
                    </div>
                  );
                }
              })()}
            </div>
            
            <div style={{
              marginTop: '1.5rem',
              textAlign: 'right'
            }}>
              <button
                onClick={toggleMetadata}
                style={{
                  backgroundColor: '#3B82F6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
{showData && (
  <div
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      zIndex: 10001,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem'
    }}
    onClick={toggleData}>
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '2rem',
        maxWidth: '90vw',
        maxHeight: '80vh',
        width: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
      }}
      onClick={(e) => e.stopPropagation()}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
        <h3
          style={{
            margin: 0,
            fontSize: '1.25rem',
            fontWeight: '600',
            color: '#1F2937'
          }}>
          Selected Data
        </h3>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            onClick={toggleData}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#6B7280',
              padding: '0.25rem'
            }}>
            ×
          </button>
        </div>
      </div>

      <div
        style={{
          flex: 1,
          overflow: 'auto',
          border: '1px solid #E5E7EB',
          borderRadius: '4px',
          backgroundColor: '#F9FAFB'
        }}>
        {tableData.length === 0 ? (
          <p
            style={{
              textAlign: 'center',
              padding: '2rem',
              color: '#6B7280',
              fontStyle: 'italic'
            }}>
            {loadingVariables > 0 ? 'Loading data…' : 'No data available for the current selection.'}
          </p>
        ) : (
          <div style={{ overflow: 'auto', maxHeight: '60vh' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#F3F4F6', zIndex: 1 }}>
                <tr>
                  {viewMode === 'single' ? (
                    <>
                      <th
                        style={{
                          border: '1px solid #D1D5DB',
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontWeight: '600',
                          cursor: 'pointer',
                          userSelect: 'none',
                          backgroundColor: '#F3F4F6'
                        }}
                        onClick={() => handleSort('Component')}>
                        System Components{' '}
                        {sortConfig.key === 'Component'
                          ? sortConfig.direction === 'asc'
                            ? ' ↑'
                            : ' ↓'
                          : ' ↕'}
                      </th>
                      <th
                        style={{
                          border: '1px solid #D1D5DB',
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontWeight: '600',
                          cursor: 'pointer',
                          userSelect: 'none',
                          backgroundColor: '#F3F4F6'
                        }}
                        onClick={() => handleSort('Sub-component')}>
                        Sub-Components{' '}
                        {sortConfig.key === 'Sub-component'
                          ? sortConfig.direction === 'asc'
                            ? ' ↑'
                            : ' ↓'
                          : ' ↕'}
                      </th>
                      <th
                        style={{
                          border: '1px solid #D1D5DB',
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontWeight: '600',
                          cursor: 'pointer',
                          userSelect: 'none',
                          backgroundColor: '#F3F4F6'
                        }}
                        onClick={() => handleSort('Variable')}>
                        Variable{' '}
                        {sortConfig.key === 'Variable'
                          ? sortConfig.direction === 'asc'
                            ? ' ↑'
                            : ' ↓'
                          : ' ↕'}
                      </th>
                      <th
                        style={{
                          border: '1px solid #D1D5DB',
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontWeight: '600',
                          cursor: 'pointer',
                          userSelect: 'none',
                          backgroundColor: '#F3F4F6'
                        }}
                        onClick={() => handleSort('Measure')}>
                        Unit of Measure{' '}
                        {sortConfig.key === 'Measure'
                          ? sortConfig.direction === 'asc'
                            ? ' ↑'
                            : ' ↓'
                          : ' ↕'}
                      </th>
                      <th
                        style={{
                          border: '1px solid #D1D5DB',
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontWeight: '600',
                          cursor: 'pointer',
                          userSelect: 'none',
                          backgroundColor: '#F3F4F6'
                        }}
                        onClick={() => handleSort('Value')}>
                        Value{' '}
                        {sortConfig.key === 'Value'
                          ? sortConfig.direction === 'asc'
                            ? ' ↑'
                            : ' ↓'
                          : ' ↕'}
                      </th>
                      <th
                        style={{
                          border: '1px solid #D1D5DB',
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontWeight: '600',
                          cursor: 'pointer',
                          userSelect: 'none',
                          backgroundColor: '#F3F4F6'
                        }}
                        onClick={() => handleSort('Year')}>
                        Year{' '}
                        {sortConfig.key === 'Year'
                          ? sortConfig.direction === 'asc'
                            ? ' ↑'
                            : ' ↓'
                          : ' ↕'}
                      </th>
                      <th
                        style={{
                          border: '1px solid #D1D5DB',
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontWeight: '600',
                          cursor: 'pointer',
                          userSelect: 'none',
                          backgroundColor: '#F3F4F6'
                        }}
                        onClick={() => handleSort('Country')}>
                        Country{' '}
                        {sortConfig.key === 'Country'
                          ? sortConfig.direction === 'asc'
                            ? ' ↑'
                            : ' ↓'
                          : ' ↕'}
                      </th>
                    </>
                  ) : (
                    <>
                      <th
                        style={{
                          border: '1px solid #D1D5DB',
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontWeight: '600',
                          cursor: 'pointer',
                          userSelect: 'none',
                          backgroundColor: '#F3F4F6'
                        }}
                        onClick={() => handleSort('Component')}>
                        System Components{' '}
                        {sortConfig.key === 'Component'
                          ? sortConfig.direction === 'asc'
                            ? ' ↑'
                            : ' ↓'
                          : ' ↕'}
                      </th>
                      <th
                        style={{
                          border: '1px solid #D1D5DB',
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontWeight: '600',
                          cursor: 'pointer',
                          userSelect: 'none',
                          backgroundColor: '#F3F4F6'
                        }}
                        onClick={() => handleSort('Sub-component')}>
                        Sub-Components{' '}
                        {sortConfig.key === 'Sub-component'
                          ? sortConfig.direction === 'asc'
                            ? ' ↑'
                            : ' ↓'
                          : ' ↕'}
                      </th>
                      <th
                        style={{
                          border: '1px solid #D1D5DB',
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontWeight: '600',
                          cursor: 'pointer',
                          userSelect: 'none',
                          backgroundColor: '#F3F4F6'
                        }}
                        onClick={() => handleSort('Variable1')}>
                        Variable 1{' '}
                        {sortConfig.key === 'Variable1'
                          ? sortConfig.direction === 'asc'
                            ? ' ↑'
                            : ' ↓'
                          : ' ↕'}
                      </th>
                      <th
                        style={{
                          border: '1px solid #D1D5DB',
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontWeight: '600',
                          cursor: 'pointer',
                          userSelect: 'none',
                          backgroundColor: '#F3F4F6'
                        }}
                        onClick={() => handleSort('Variable2')}>
                        Variable 2{' '}
                        {sortConfig.key === 'Variable2'
                          ? sortConfig.direction === 'asc'
                            ? ' ↑'
                            : ' ↓'
                          : ' ↕'}
                      </th>
                      <th
                        style={{
                          border: '1px solid #D1D5DB',
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontWeight: '600',
                          cursor: 'pointer',
                          userSelect: 'none',
                          backgroundColor: '#F3F4F6'
                        }}
                        onClick={() => handleSort('Measure1')}>
                        Unit 1{' '}
                        {sortConfig.key === 'Measure1'
                          ? sortConfig.direction === 'asc'
                            ? ' ↑'
                            : ' ↓'
                          : ' ↕'}
                      </th>
                      <th
                        style={{
                          border: '1px solid #D1D5DB',
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontWeight: '600',
                          cursor: 'pointer',
                          userSelect: 'none',
                          backgroundColor: '#F3F4F6'
                        }}
                        onClick={() => handleSort('Measure2')}>
                        Unit 2{' '}
                        {sortConfig.key === 'Measure2'
                          ? sortConfig.direction === 'asc'
                            ? ' ↑'
                            : ' ↓'
                          : ' ↕'}
                      </th>
                      <th
                        style={{
                          border: '1px solid #D1D5DB',
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontWeight: '600',
                          cursor: 'pointer',
                          userSelect: 'none',
                          backgroundColor: '#F3F4F6'
                        }}
                        onClick={() => handleSort('Value1')}>
                        Value 1{' '}
                        {sortConfig.key === 'Value1'
                          ? sortConfig.direction === 'asc'
                            ? ' ↑'
                            : ' ↓'
                          : ' ↕'}
                      </th>
                      <th
                        style={{
                          border: '1px solid #D1D5DB',
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontWeight: '600',
                          cursor: 'pointer',
                          userSelect: 'none',
                          backgroundColor: '#F3F4F6'
                        }}
                        onClick={() => handleSort('Value2')}>
                        Value 2{' '}
                        {sortConfig.key === 'Value2'
                          ? sortConfig.direction === 'asc'
                            ? ' ↑'
                            : ' ↓'
                          : ' ↕'}
                      </th>
                      <th
                        style={{
                          border: '1px solid #D1D5DB',
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontWeight: '600',
                          cursor: 'pointer',
                          userSelect: 'none',
                          backgroundColor: '#F3F4F6'
                        }}
                        onClick={() => handleSort('Year')}>
                        Year{' '}
                        {sortConfig.key === 'Year'
                          ? sortConfig.direction === 'asc'
                            ? ' ↑'
                            : ' ↓'
                          : ' ↕'}
                      </th>
                      <th
                        style={{
                          border: '1px solid #D1D5DB',
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontWeight: '600',
                          cursor: 'pointer',
                          userSelect: 'none',
                          backgroundColor: '#F3F4F6'
                        }}
                        onClick={() => handleSort('Country')}>
                        Country{' '}
                        {sortConfig.key === 'Country'
                          ? sortConfig.direction === 'asc'
                            ? ' ↑'
                            : ' ↓'
                          : ' ↕'}
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, index) => (
                  <tr
                    key={index}
                    style={{ backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F9FAFB' }}>
                    {viewMode === 'single' ? (
                      <>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px', verticalAlign: 'top', textAlign: 'left' }}>
                          {row.Component || 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px', verticalAlign: 'top', textAlign: 'left' }}>
                          {row['Sub-component'] || 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px', verticalAlign: 'top', textAlign: 'left' }}>
                          {row.Variable || 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px', verticalAlign: 'top', textAlign: 'left' }}>
                          {row.Measure || 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px', verticalAlign: 'top', textAlign: 'left' }}>
                          {typeof row.Value === 'number' && !isNaN(row.Value)
                            ? row.Value.toLocaleString()
                            : row.Value || 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px', verticalAlign: 'top', textAlign: 'left' }}>
                          {row.Year || 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px', verticalAlign: 'top', textAlign: 'left' }}>
                          {row.Country || 'N/A'}
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px', verticalAlign: 'top', textAlign: 'left' }}>
                          {row.Component || 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px', verticalAlign: 'top', textAlign: 'left' }}>
                          {row['Sub-component'] || 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px', verticalAlign: 'top', textAlign: 'left' }}>
                          {row.Variable1 || 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px', verticalAlign: 'top', textAlign: 'left' }}>
                          {row.Variable2 || 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px', verticalAlign: 'top', textAlign: 'left' }}>
                          {row.Measure1 || 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px', verticalAlign: 'top', textAlign: 'left' }}>
                          {row.Measure2 || 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px', verticalAlign: 'top', textAlign: 'left' }}>
                          {row.Value1 !== null && typeof row.Value1 === 'number' && !isNaN(row.Value1)
                            ? row.Value1.toLocaleString()
                            : 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px', verticalAlign: 'top', textAlign: 'left' }}>
                          {row.Value2 !== null && typeof row.Value2 === 'number' && !isNaN(row.Value2)
                            ? row.Value2.toLocaleString()
                            : 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px', verticalAlign: 'top', textAlign: 'left' }}>
                          {row.Year || 'N/A'}
                        </td>
                        <td style={{ border: '1px solid #E5E7EB', padding: '8px', verticalAlign: 'top', textAlign: 'left' }}>
                          {row.Country || 'N/A'}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div
        style={{
          marginTop: '1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '0.875rem',
          color: '#6B7280'
        }}>
        <span>
          Showing {tableData.length} {tableData.length === 1 ? 'record' : 'records'}
          {activeTab === 'map' && selectedYear && ` for ${selectedYear}`}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {maxValue > 0 && <span>Max value: {maxValue.toLocaleString()}</span>}
        </div>
      </div>
    </div>
  </div>
)}

      {/* Tooltips for primary dropdowns */}
      <Tooltip
        key={fontLoaded ? 'font-loaded-component' : 'font-loading-component'} 
        id="component-tooltip"
        place="right"
        style={{ backgroundColor: '#1F2937', color: '#FFFFFF', maxWidth: '300px', zIndex: 9999 }}
        afterShow={() => console.log('Tooltip shown: component-tooltip')}
      />
      <Tooltip
        key={fontLoaded ? 'font-loaded-subcomponent' : 'font-loading-subcomponent'} 
        id="subcomponent-tooltip"
        place="right"
        style={{ backgroundColor: '#1F2937', color: '#FFFFFF', maxWidth: '300px', zIndex: 9999 }}
        afterShow={() => console.log('Tooltip shown: subcomponent-tooltip')}
      />
      <Tooltip
        key={fontLoaded ? 'font-loaded-variable' : 'font-loading-variable'} 
        id="variable-tooltip"
        place="right"
        style={{ backgroundColor: '#1F2937', color: '#FFFFFF', maxWidth: '300px', zIndex: 9999 }}
        afterShow={() => console.log('Tooltip shown: variable-tooltip')}
      />
      <Tooltip
        key={fontLoaded ? 'font-loaded-measure' : 'font-loading-measure'} 
        id="measure-tooltip"
        place="right"
        style={{ backgroundColor: '#1F2937', color: '#FFFFFF', maxWidth: '300px', zIndex: 9999 }}
        afterShow={() => console.log('Tooltip shown: measure-tooltip')}
      />
      
      {/* Tooltips for secondary dropdowns (comparative mode) */}
      <Tooltip
        id="component2-tooltip"
        place="right"
        style={{ backgroundColor: '#1F2937', color: '#FFFFFF', maxWidth: '300px', zIndex: 9999 }}
        afterShow={() => console.log('Tooltip shown: component2-tooltip')}
      />
      <Tooltip
        id="subcomponent2-tooltip"
        place="right"
        style={{ backgroundColor: '#1F2937', color: '#FFFFFF', maxWidth: '300px', zIndex: 9999 }}
        afterShow={() => console.log('Tooltip shown: subcomponent2-tooltip')}
      />
      <Tooltip
        id="variable2-tooltip"
        place="right"
        style={{ backgroundColor: '#1F2937', color: '#FFFFFF', maxWidth: '300px', zIndex: 9999 }}
        afterShow={() => console.log('Tooltip shown: variable2-tooltip')}
      />
      <Tooltip
        id="measure2-tooltip"
        place="right"
        style={{ backgroundColor: '#1F2937', color: '#FFFFFF', maxWidth: '300px', zIndex: 9999 }}
        afterShow={() => console.log('Tooltip shown: measure2-tooltip')}
      />
      
      {/* Header variable tooltip */}
      <Tooltip
        id="header-variable-tooltip"
        place="bottom"
        style={{ backgroundColor: '#1F2937', color: '#FFFFFF', maxWidth: '400px', zIndex: 9999, whiteSpace: 'pre-line' }}
        afterShow={() => console.log('Tooltip shown: header-variable-tooltip')}
      />
    </>
  );
}

export default OECDExplorer;